import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  AreaChart,
  Area,
} from "recharts";
import { ArrowDownRight, ArrowUpRight, AlertTriangle, Activity, DollarSign, Server, ShieldAlert } from "lucide-react";

const ROLE_OPTIONS = [
  { value: "owner", label: "Firma Sahibi" },
  { value: "admin", label: "Yönetici" },
  { value: "manager", label: "Müdür" },
  { value: "operator", label: "Operasyon" },
  { value: "accountant", label: "Muhasebe" },
  { value: "support", label: "Destek" },
  { value: "viewer", label: "Görüntüleyici" },
];
const STATUS_OPTIONS = ["active", "expired", "suspended", "cancelled"];
const SUBSCRIPTION_PLAN_OPTIONS = [
  { value: "trial", label: "Deneme (Trial)" },
  { value: "starter", label: "Starter" },
  { value: "growth", label: "Pro (Growth)" },
  { value: "enterprise", label: "Enterprise" },
];
const normalizePlanKey = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "pro") return "growth";
  if (["trial", "starter", "growth", "enterprise"].includes(raw)) return raw;
  return "trial";
};
const normalizeRoleKey = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  return ROLE_OPTIONS.some((r) => r.value === raw) ? raw : "viewer";
};
const normalizeStatusKey = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  return STATUS_OPTIONS.includes(raw) ? raw : "active";
};
const normalizeCycleKey = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  return raw === "yearly" ? "yearly" : "monthly";
};
const TABS = [
  { key: "overview", label: "Genel Durum" },
  { key: "users", label: "Kullanıcı & Yetki" },
  { key: "usage", label: "Kullanım Analizi" },
  { key: "errors", label: "Hata İzleme" },
  { key: "support", label: "Destek Merkezi" },
  { key: "settings", label: "Ayarlar" },
];
const REFERRAL_PLAN_OPTIONS = ["starter", "growth", "enterprise"];
const PERMISSION_LABELS = {
  "dashboard.view": "Genel Bakış Görüntüleme",
  "catalog.view": "Ürünleri Görüntüleme",
  "catalog.manage": "Ürünleri Yönetme",
  "orders.view": "Siparişleri Görüntüleme",
  "orders.manage": "Siparişleri Yönetme",
  "integrations.view": "Entegrasyonları Görüntüleme",
  "integrations.manage": "Entegrasyonları Yönetme",
  "inventory.view": "Stok & Depo Görüntüleme",
  "inventory.manage": "Stok & Depo Yönetme",
  "reports.view": "Raporları Görüntüleme",
  "customers.view": "Müşterileri Görüntüleme",
  "customers.manage": "Müşterileri Yönetme",
  "billing.view": "Abonelik & Fatura Görüntüleme",
  "billing.manage": "Abonelik & Fatura Yönetme",
  "settings.view": "Ayarları Görüntüleme",
  "settings.manage": "Ayarları Yönetme",
  "logs.view": "Logları Görüntüleme",
};
const MODULE_LABELS = {
  dashboard: "Genel Bakış",
  integrations: "Entegrasyonlar",
  orders: "Siparişler",
  catalog: "Ürünler",
  inventory: "Stok & Depo",
  customers: "Müşteriler",
  settings: "Ayarlar",
  error_tracking: "Hata İzleme",
  auth: "Kimlik Doğrulama",
};
const UNLIMITED_VALUE = 1000000;
const PLAN_PRICE_MAP = { trial: 0, starter: 29, growth: 99, enterprise: 499 };
const PIE_COLORS = ["#38bdf8", "#34d399", "#f59e0b", "#f97316", "#ef4444"];
const SETTINGS_SECTIONS = [
  { key: "integrations", label: "Uygulama & Entegrasyon", hint: "Modul anahtarlari ve tenant override" },
  { key: "packages", label: "Paket & Faturalama", hint: "Paket icerikleri ve fiyat yapisi" },
  { key: "payments", label: "Odeme", hint: "PayTR konfigrasyonu" },
  { key: "media", label: "R2 Medya", hint: "Bucket ve public URL ayarlari" },
  { key: "communications", label: "Iletisim", hint: "Mail SES, loglar ve template" },
  { key: "ai", label: "AI Servisi", hint: "Anthropic, OpenAI, Gemini API anahtarlari" },
  { key: "admin", label: "Super Admin", hint: "Admin hesaplari, yetki ve sifre" },
];

const toNum = (v) => Number(v || 0);
const pctChange = (current, previous) => {
  const c = toNum(current);
  const p = toNum(previous);
  if (p === 0) return c > 0 ? 100 : 0;
  return ((c - p) / p) * 100;
};
const trendBadge = (value) => {
  const v = Number(value || 0);
  const up = v >= 0;
  return {
    text: `${up ? "+" : ""}${v.toFixed(1)}%`,
    cls: up ? "text-emerald-300" : "text-rose-300",
    icon: up ? ArrowUpRight : ArrowDownRight,
  };
};

const getPlanBaseLimits = (plan, status) => {
  const normalizedPlan = String(plan || "trial").toLowerCase();
  const normalizedStatus = String(status || "active").toLowerCase();
  if (["expired", "suspended", "cancelled"].includes(normalizedStatus)) {
    return {
      max_store_integrations: 0,
      max_marketplace_integrations: 0,
      max_cargo_integrations: 0,
      max_accounting_integrations: 0,
      max_integrations: 0,
      max_products: 0,
      max_orders_monthly: 0,
      max_api_calls_daily: 0,
    };
  }
  if (normalizedPlan === "starter") {
    return {
      max_store_integrations: 1,
      max_marketplace_integrations: 1,
      max_cargo_integrations: 1,
      max_accounting_integrations: 1,
      max_integrations: 1,
      max_products: 3000,
      max_orders_monthly: 3000,
      max_api_calls_daily: 30000,
    };
  }
  if (normalizedPlan === "growth") {
    return {
      max_store_integrations: 1,
      max_marketplace_integrations: 3,
      max_cargo_integrations: 3,
      max_accounting_integrations: 1,
      max_integrations: 3,
      max_products: 25000,
      max_orders_monthly: 20000,
      max_api_calls_daily: 100000,
    };
  }
  if (normalizedPlan === "enterprise" || normalizedPlan === "trial") {
    return {
      max_store_integrations: UNLIMITED_VALUE,
      max_marketplace_integrations: UNLIMITED_VALUE,
      max_cargo_integrations: UNLIMITED_VALUE,
      max_accounting_integrations: UNLIMITED_VALUE,
      max_integrations: UNLIMITED_VALUE,
      max_products: 100000000,
      max_orders_monthly: 100000000,
      max_api_calls_daily: 100000000,
    };
  }
  return getPlanBaseLimits("trial", status);
};

const formatLimit = (v) => (Number(v) >= UNLIMITED_VALUE ? "Sınırsız" : String(v ?? 0));

function toDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function SuperPanelPage({ profile }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [insights, setInsights] = useState(null);
  const [errorLogs, setErrorLogs] = useState([]);
  const [usageUsers, setUsageUsers] = useState([]);
  const [usageDetail, setUsageDetail] = useState(null);
  const [usageDays, setUsageDays] = useState(30);
  const [usageActiveHours, setUsageActiveHours] = useState(24);
  const [usageLoading, setUsageLoading] = useState(false);
  const [selectedUsageUserId, setSelectedUsageUserId] = useState(null);
  const [usageSearch, setUsageSearch] = useState("");
  const [usageRoleFilter, setUsageRoleFilter] = useState("all");
  const [usageStatusFilter, setUsageStatusFilter] = useState("all");
  const [usagePlanFilter, setUsagePlanFilter] = useState("all");
  const [usageModuleFilter, setUsageModuleFilter] = useState("all");
  const [usageMinEvents, setUsageMinEvents] = useState(0);
  const [errorStatusFilter, setErrorStatusFilter] = useState("open");
  const [errorSeverityFilter, setErrorSeverityFilter] = useState("all");
  const [errorSearch, setErrorSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingCompany, setDeletingCompany] = useState(false);
  const [referralSaving, setReferralSaving] = useState(false);
  const [referralAllowedPlans, setReferralAllowedPlans] = useState([]);
  const [packageSaving, setPackageSaving] = useState(false);
  const [billingPackages, setBillingPackages] = useState({
    title: "",
    subtitle: "",
    trial_days: 15,
    packages: [],
  });
  const [mailSettings, setMailSettings] = useState({
    enabled: false,
    provider: "aws_ses",
    aws_region: "",
    sender_email: "",
    sender_name: "",
    reply_to_email: "",
    aws_access_key_id: "",
    aws_secret_access_key: "",
    configuration_set_name: "",
    source_arn: "",
  });
  const [paytrSettings, setPaytrSettings] = useState({
    enabled: false,
    merchant_id: "",
    merchant_key: "",
    merchant_salt: "",
    test_mode: 1,
    merchant_ok_url: "",
    merchant_fail_url: "",
    save_card_default: false,
    strict_mode: true,
    merchant_id_masked: "",
    merchant_key_masked: "",
    merchant_salt_masked: "",
  });
  const [paytrSettingsSaving, setPaytrSettingsSaving] = useState(false);
  const [mediaR2Saving, setMediaR2Saving] = useState(false);
  const [mediaR2Settings, setMediaR2Settings] = useState({
    enabled: true,
    r2_endpoint_url: "",
    r2_public_base_url: "",
    r2_access_key_id: "",
    r2_secret_access_key: "",
    r2_access_key_id_masked: "",
    r2_secret_access_key_masked: "",
    has_r2_access_key_id: false,
    has_r2_secret_access_key: false,
    auto_bucket_by_domain: false,
    auto_create_bucket: false,
    bucket_prefix: "obihub-media",
    key_prefix: "product-images",
    default_bucket: "",
    default_public_base_url: "",
    public_base_url_template: "",
    domain_buckets: [],
  });
  const [mailSettingsSaving, setMailSettingsSaving] = useState(false);
  const [mailLogs, setMailLogs] = useState([]);
  const [mailLogsLoading, setMailLogsLoading] = useState(false);
  const [mailLogSearch, setMailLogSearch] = useState("");
  const [mailLogStatus, setMailLogStatus] = useState("all");
  const [testMailTo, setTestMailTo] = useState("");
  const [testMailSending, setTestMailSending] = useState(false);
  const [mailTemplates, setMailTemplates] = useState([]);
  const [mailTemplatesLoading, setMailTemplatesLoading] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateTestTo, setTemplateTestTo] = useState("");
  const [templateTestSending, setTemplateTestSending] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    id: null,
    key: "",
    name: "",
    category: "custom",
    description: "",
    is_active: true,
    subject_template: "",
    html_template: "",
    text_template: "",
  });
  const [selectedId, setSelectedId] = useState(null);
  const [globalSearch, setGlobalSearch] = useState("");
  const [quickTenantId, setQuickTenantId] = useState("");
  const [quickActionLoading, setQuickActionLoading] = useState(false);
  const [overviewRangeDays, setOverviewRangeDays] = useState(30);
  const [overviewAutoRefresh, setOverviewAutoRefresh] = useState(true);
  const [overviewRefreshSeconds, setOverviewRefreshSeconds] = useState(45);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewStats, setOverviewStats] = useState({
    api_24h: 0,
    api_prev_24h: 0,
    error_24h: 0,
    error_prev_24h: 0,
    error_rate_24h: 0,
    critical_open: 0,
    warning_open: 0,
    spike_last_hour: 0,
    top_error_endpoint: "-",
    mrr_current: 0,
    mrr_previous_month: 0,
  });
  const [mrrTrendRows, setMrrTrendRows] = useState([]);
  const [errorTrendRows, setErrorTrendRows] = useState([]);
  const [hourlyApiRows, setHourlyApiRows] = useState([]);
  const [packageCards, setPackageCards] = useState([]);
  const [tenantIntelRows, setTenantIntelRows] = useState([]);
  const [moduleUsageRows, setModuleUsageRows] = useState([]);
  const [endpointUsageRows, setEndpointUsageRows] = useState([]);
  const [usageAutoRefresh, setUsageAutoRefresh] = useState(true);
  const [usageRefreshSeconds, setUsageRefreshSeconds] = useState(30);
  const [usageLastUpdatedAt, setUsageLastUpdatedAt] = useState(null);
  const [errorsAutoRefresh, setErrorsAutoRefresh] = useState(true);
  const [errorsRefreshSeconds, setErrorsRefreshSeconds] = useState(30);
  const [errorsLastUpdatedAt, setErrorsLastUpdatedAt] = useState(null);
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportStatusFilter, setSupportStatusFilter] = useState("all");
  const [supportSearch, setSupportSearch] = useState("");
  const [selectedSupportTicketId, setSelectedSupportTicketId] = useState("");
  const [selectedSupportDetail, setSelectedSupportDetail] = useState(null);
  const [supportMessages, setSupportMessages] = useState([]);
  const [supportReply, setSupportReply] = useState("");
  const [supportSaving, setSupportSaving] = useState(false);
  const criticalErrorMarkerRef = useRef("");
  const supportTicketsInFlightRef = useRef(false);
  const supportDetailInFlightRef = useRef(false);
  const supportTicketsLastFetchAtRef = useRef(0);
  const supportDetailLastFetchAtRef = useRef(0);
  const supportUnreadCount = useMemo(
    () => supportTickets.reduce((acc, row) => acc + (row?.unread_from_customer ? 1 : 0), 0),
    [supportTickets]
  );
  const [providerRows, setProviderRows] = useState([]);
  const [providerTenantId, setProviderTenantId] = useState("");
  const [providerLoading, setProviderLoading] = useState(false);
  const [providerSavingKey, setProviderSavingKey] = useState("");
  const [providerClassFilter, setProviderClassFilter] = useState("all");
  const [settingsSection, setSettingsSection] = useState("integrations");
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [adminUserSaving, setAdminUserSaving] = useState(false);
  const [selectedAdminUserId, setSelectedAdminUserId] = useState("");
  const [adminUserForm, setAdminUserForm] = useState({
    email: "",
    full_name: "",
    password: "",
    is_active: true,
    permissions_raw: "",
  });
  const [myAccountForm, setMyAccountForm] = useState({
    email: "",
    full_name: "",
    current_password: "",
    new_password: "",
    new_password_confirm: "",
  });
  const [form, setForm] = useState({
    role: "viewer",
    subscription_plan: "trial",
    subscription_status: "active",
    subscription_billing_cycle: "monthly",
    subscription_expires_at: "",
    max_store_integrations: 1,
    max_marketplace_integrations: 1,
    max_cargo_integrations: 1,
    max_accounting_integrations: 1,
    max_integrations: 1,
    max_products: 1000,
    max_orders_monthly: 1000,
    max_api_calls_daily: 10000,
    limit_change_note: "",
    permissions: [],
  });
  const [trialExtendDays, setTrialExtendDays] = useState(7);
  const [trialExtendReason, setTrialExtendReason] = useState("");
  const [trialHistory, setTrialHistory] = useState([]);
  const [checkoutDeleteFeedback, setCheckoutDeleteFeedback] = useState([]);
  const [limitAuditLogs, setLimitAuditLogs] = useState([]);
  const [trialExtending, setTrialExtending] = useState(false);
  const [tempIncrease, setTempIncrease] = useState({
    max_store_integrations: 0,
    max_marketplace_integrations: 0,
    max_cargo_integrations: 0,
    max_accounting_integrations: 0,
    max_integrations: 0,
    max_products: 0,
    max_orders_monthly: 0,
    max_api_calls_daily: 0,
  });

  const selectedUser = useMemo(() => users.find((u) => u.id === selectedId) || null, [users, selectedId]);
  const selectedProviderTenant = useMemo(
    () => users.find((u) => u.id === providerTenantId) || null,
    [users, providerTenantId]
  );
  const selectedAdminUser = useMemo(
    () => adminUsers.find((u) => u.id === selectedAdminUserId) || null,
    [adminUsers, selectedAdminUserId]
  );
  const visibleProviderRows = useMemo(() => {
    if (providerClassFilter === "all") return providerRows;
    return providerRows.filter((x) => String(x?.module_class || "") === providerClassFilter);
  }, [providerRows, providerClassFilter]);
  const baseLimits = useMemo(
    () => getPlanBaseLimits(normalizePlanKey(form.subscription_plan), form.subscription_status),
    [form.subscription_plan, form.subscription_status]
  );

  const handleLogout = () => {
    localStorage.removeItem("obihub_super_token");
    navigate("/super-giris");
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [mods, list, ins, referralCfg] = await Promise.all([
        api.get("/super/modules"),
        api.get("/super/users", { params: { search: q || undefined, per_page: 200 } }),
        api.get("/super/insights", { params: { risk_limit: 12 } }),
        api.get("/super/referrals/config"),
      ]);
      setModules(Array.isArray(mods) ? mods : []);
      setInsights(ins || null);
      if (Array.isArray(referralCfg?.allowed_plans)) setReferralAllowedPlans(referralCfg.allowed_plans);
      const items = Array.isArray(list?.items) ? list.items : [];
      setUsers(items);
      if (items.length > 0) {
        const keep = items.find((u) => u.id === selectedId);
        const next = keep || items[0];
        setSelectedId(next.id);
        setProviderTenantId((prev) => prev || next.id);
      } else {
        setSelectedId(null);
        setProviderTenantId("");
      }
    } catch (err) {
      toast.error(err.message || "Kullanıcılar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const loadBillingPackages = async () => {
    try {
      const res = await api.get("/super/billing/packages");
      setBillingPackages({
        title: String(res?.title || ""),
        subtitle: String(res?.subtitle || ""),
        trial_days: Number(res?.trial_days || 15),
        packages: Array.isArray(res?.packages) ? res.packages : [],
      });
    } catch (err) {
      toast.error(err.message || "Paketler yüklenemedi");
    }
  };

  const loadTrialHistory = async (userId) => {
    if (!userId) {
      setTrialHistory([]);
      return;
    }
    try {
      const res = await api.get(`/super/users/${userId}/trial-extensions`);
      setTrialHistory(Array.isArray(res?.items) ? res.items : []);
    } catch {
      setTrialHistory([]);
    }
  };

  const loadCheckoutDeleteFeedback = async (userId) => {
    if (!userId) {
      setCheckoutDeleteFeedback([]);
      return;
    }
    try {
      const res = await api.get(`/super/users/${userId}/checkout-intent-delete-feedback`);
      setCheckoutDeleteFeedback(Array.isArray(res?.items) ? res.items : []);
    } catch {
      setCheckoutDeleteFeedback([]);
    }
  };

  const loadLimitAuditLogs = async (userId) => {
    if (!userId) {
      setLimitAuditLogs([]);
      return;
    }
    try {
      const res = await api.get(`/super/users/${userId}/limit-audit`);
      setLimitAuditLogs(Array.isArray(res?.items) ? res.items : []);
    } catch {
      setLimitAuditLogs([]);
    }
  };

  const loadErrorLogs = async () => {
    try {
      const params = {
        per_page: 50,
        status: errorStatusFilter === "all" ? undefined : errorStatusFilter,
        severity: errorSeverityFilter === "all" ? undefined : errorSeverityFilter,
        search: errorSearch || undefined,
      };
      const res = await api.get("/super/error-logs", { params });
      setErrorLogs(Array.isArray(res?.items) ? res.items : []);
      setErrorsLastUpdatedAt(new Date().toISOString());
    } catch (err) {
      toast.error(err.message || "Hata logları yüklenemedi");
    }
  };

  const loadSupportTickets = async ({ force = false } = {}) => {
    const now = Date.now();
    if (supportTicketsInFlightRef.current) return;
    if (!force && now - supportTicketsLastFetchAtRef.current < 15000) return;
    supportTicketsInFlightRef.current = true;
    supportTicketsLastFetchAtRef.current = now;
    setSupportLoading(true);
    try {
      const params = {
        page: 1,
        per_page: 150,
        status: supportStatusFilter === "all" ? undefined : supportStatusFilter,
        search: supportSearch || undefined,
      };
      const res = await api.get("/super/support/tickets", { params });
      const rows = Array.isArray(res?.items) ? res.items : [];
      setSupportTickets(rows);
      if (!selectedSupportTicketId && rows.length) {
        setSelectedSupportTicketId(rows[0].id);
      } else if (selectedSupportTicketId && !rows.some((x) => x.id === selectedSupportTicketId)) {
        setSelectedSupportTicketId(rows[0]?.id || "");
      }
    } catch (err) {
      toast.error(err.message || "Destek kayıtları yüklenemedi");
    } finally {
      supportTicketsInFlightRef.current = false;
      setSupportLoading(false);
    }
  };

  const loadSupportTicketDetail = async (ticketId, { force = false } = {}) => {
    if (!ticketId) {
      setSelectedSupportDetail(null);
      setSupportMessages([]);
      return;
    }
    const now = Date.now();
    if (supportDetailInFlightRef.current) return;
    if (!force && now - supportDetailLastFetchAtRef.current < 8000) return;
    supportDetailInFlightRef.current = true;
    supportDetailLastFetchAtRef.current = now;
    try {
      const res = await api.get(`/super/support/tickets/${ticketId}`);
      setSelectedSupportDetail(res?.ticket || null);
      setSupportMessages(Array.isArray(res?.messages) ? res.messages : []);
      setSupportTickets((prev) => prev.map((x) => (x.id === ticketId ? { ...x, unread_from_customer: false } : x)));
    } catch (err) {
      toast.error(err.message || "Destek detayları yüklenemedi");
    } finally {
      supportDetailInFlightRef.current = false;
    }
  };

  const superReplySupportTicket = async () => {
    const text = String(supportReply || "").trim();
    if (!selectedSupportTicketId || text.length < 2) return;
    setSupportSaving(true);
    try {
      await api.post(`/super/support/tickets/${selectedSupportTicketId}/messages`, { message: text });
      setSupportReply("");
      await Promise.all([
        loadSupportTickets({ force: true }),
        loadSupportTicketDetail(selectedSupportTicketId, { force: true }),
      ]);
      toast.success("Destek yanıtı gönderildi");
    } catch (err) {
      toast.error(err.message || "Destek yanıtı gönderilemedi");
    } finally {
      setSupportSaving(false);
    }
  };

  const superChangeSupportStatus = async (statusValue) => {
    if (!selectedSupportTicketId) return;
    try {
      await api.patch(`/super/support/tickets/${selectedSupportTicketId}/status`, { status: statusValue });
      await Promise.all([
        loadSupportTickets({ force: true }),
        loadSupportTicketDetail(selectedSupportTicketId, { force: true }),
      ]);
      toast.success("Destek durumu güncellendi");
    } catch (err) {
      toast.error(err.message || "Destek durumu güncellenemedi");
    }
  };

  const loadUsageUsers = async () => {
    setUsageLoading(true);
    try {
      const res = await api.get("/super/usage/users", {
        params: {
          days: usageDays,
          active_hours: usageActiveHours,
          search: usageSearch || undefined,
          role: usageRoleFilter === "all" ? undefined : usageRoleFilter,
          subscription_status: usageStatusFilter === "all" ? undefined : usageStatusFilter,
          subscription_plan: usagePlanFilter === "all" ? undefined : usagePlanFilter,
          module: usageModuleFilter === "all" ? undefined : usageModuleFilter,
          min_events: Number(usageMinEvents || 0),
          per_page: 300,
        },
      });
      const items = Array.isArray(res?.items) ? res.items : [];
      setUsageUsers(items);
      setUsageLastUpdatedAt(new Date().toISOString());
      if (items.length > 0) {
        const keep = items.find((x) => x.user_id === selectedUsageUserId);
        setSelectedUsageUserId((keep || items[0]).user_id);
      } else {
        setSelectedUsageUserId(null);
        setUsageDetail(null);
      }
    } catch (err) {
      toast.error(err.message || "Kullanım analizi yüklenemedi");
    } finally {
      setUsageLoading(false);
    }
  };

  const loadUsageDetail = async (userId) => {
    if (!userId) return;
    try {
      const res = await api.get(`/super/usage/users/${userId}`, { params: { days: usageDays } });
      setUsageDetail(res || null);
    } catch (err) {
      toast.error(err.message || "Kullanım detayı alınamadı");
    }
  };

  const loadMailSettings = async () => {
    try {
      const res = await api.get("/super/mail-settings");
      setMailSettings((prev) => ({
        ...prev,
        ...res,
        aws_access_key_id: "",
        aws_secret_access_key: "",
      }));
    } catch (err) {
      toast.error(err.message || "Mail ayarları yüklenemedi");
    }
  };

  const loadPaytrSettings = async () => {
    try {
      const res = await api.get("/super/paytr-settings");
      setPaytrSettings((prev) => ({
        ...prev,
        ...res,
        merchant_id: "",
        merchant_key: "",
        merchant_salt: "",
      }));
    } catch (err) {
      toast.error(err.message || "PayTR ayarları yüklenemedi");
    }
  };

  const loadMediaR2Settings = async () => {
    try {
      const res = await api.get("/super/media-r2-settings");
      const rows = Object.entries(res?.domain_buckets || {}).map(([domain, row]) => ({
        domain: String(domain || "").trim().toLowerCase(),
        bucket: String(row?.bucket || "").trim(),
        public_base_url: String(row?.public_base_url || "").trim(),
      }));
      setMediaR2Settings({
        enabled: Boolean(res?.enabled ?? true),
        r2_endpoint_url: String(res?.r2_endpoint_url || ""),
        r2_public_base_url: String(res?.r2_public_base_url || ""),
        r2_access_key_id: "",
        r2_secret_access_key: "",
        r2_access_key_id_masked: String(res?.r2_access_key_id_masked || ""),
        r2_secret_access_key_masked: String(res?.r2_secret_access_key_masked || ""),
        has_r2_access_key_id: Boolean(res?.has_r2_access_key_id),
        has_r2_secret_access_key: Boolean(res?.has_r2_secret_access_key),
        auto_bucket_by_domain: Boolean(res?.auto_bucket_by_domain),
        auto_create_bucket: Boolean(res?.auto_create_bucket),
        bucket_prefix: String(res?.bucket_prefix || "obihub-media"),
        key_prefix: String(res?.key_prefix || "product-images"),
        default_bucket: String(res?.default_bucket || ""),
        default_public_base_url: String(res?.default_public_base_url || ""),
        public_base_url_template: String(res?.public_base_url_template || ""),
        domain_buckets: rows,
      });
    } catch (err) {
      toast.error(err.message || "R2 ayarlari yuklenemedi");
    }
  };

  const loadMailLogs = async () => {
    setMailLogsLoading(true);
    try {
      const res = await api.get("/super/mail-logs", {
        params: {
          page: 1,
          per_page: 50,
          status: mailLogStatus === "all" ? undefined : mailLogStatus,
          search: mailLogSearch || undefined,
        },
      });
      setMailLogs(Array.isArray(res?.items) ? res.items : []);
    } catch (err) {
      toast.error(err.message || "Mail logları alınamadı");
    } finally {
      setMailLogsLoading(false);
    }
  };

  const loadMailTemplates = async () => {
    setMailTemplatesLoading(true);
    try {
      const res = await api.get("/super/mail-templates");
      const items = Array.isArray(res?.items) ? res.items : [];
      setMailTemplates(items);
      if (items.length && !templateForm.id) {
        const first = items[0];
        setTemplateForm({
          id: first.id,
          key: first.key || "",
          name: first.name || "",
          category: first.category || "custom",
          description: first.description || "",
          is_active: Boolean(first.is_active),
          subject_template: first.subject_template || "",
          html_template: first.html_template || "",
          text_template: first.text_template || "",
        });
      }
    } catch (err) {
      toast.error(err.message || "Mail şablonları alınamadı");
    } finally {
      setMailTemplatesLoading(false);
    }
  };

  const loadAdminUsers = async () => {
    setAdminUsersLoading(true);
    try {
      const res = await api.get("/super/admin-users");
      const items = Array.isArray(res?.items) ? res.items : [];
      setAdminUsers(items);
      if (!selectedAdminUserId && items.length) {
        setSelectedAdminUserId(items[0].id);
      }
    } catch (err) {
      toast.error(err.message || "Super admin kullanıcıları yüklenemedi");
    } finally {
      setAdminUsersLoading(false);
    }
  };

  const loadProviderControls = async (tenantId) => {
    setProviderLoading(true);
    try {
      const params = {};
      const uid = String(tenantId || "").trim();
      if (uid) params.user_id = uid;
      const res = await api.get("/super/providers", { params });
      setProviderRows(Array.isArray(res?.items) ? res.items : []);
    } catch (err) {
      toast.error(err.message || "Modul listesi yuklenemedi");
    } finally {
      setProviderLoading(false);
    }
  };

  const updateGlobalProvider = async (providerKey, enabled) => {
    setProviderSavingKey(`global:${providerKey}`);
    try {
      const res = await api.patch("/super/providers/global", {
        providers: [{ key: providerKey, enabled: Boolean(enabled) }],
      });
      setProviderRows(Array.isArray(res?.items) ? res.items : []);
      toast.success("Global modul durumu guncellendi");
    } catch (err) {
      toast.error(err.message || "Global modul guncellenemedi");
    } finally {
      setProviderSavingKey("");
    }
  };

  const updateTenantProvider = async (providerKey, enabledOrNull) => {
    if (!providerTenantId) {
      toast.error("Lutfen once bir tenant secin");
      return;
    }
    setProviderSavingKey(`tenant:${providerKey}`);
    try {
      const res = await api.patch(`/super/users/${providerTenantId}/providers`, {
        providers: [{ key: providerKey, enabled: enabledOrNull }],
      });
      setProviderRows(Array.isArray(res?.items) ? res.items : []);
      toast.success("Tenant modul override guncellendi");
    } catch (err) {
      toast.error(err.message || "Tenant modul override guncellenemedi");
    } finally {
      setProviderSavingKey("");
    }
  };

  useEffect(() => {
    loadData();
    loadBillingPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadErrorLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorStatusFilter, errorSeverityFilter]);

  useEffect(() => {
    if (activeTab !== "usage") return;
    loadUsageUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, usageDays]);
  useEffect(() => {
    if (activeTab !== "usage") return;
    loadUsageUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usageActiveHours, usageSearch, usageRoleFilter, usageStatusFilter, usagePlanFilter, usageModuleFilter, usageMinEvents]);

  useEffect(() => {
    if (activeTab !== "usage") return;
    if (!selectedUsageUserId) return;
    loadUsageDetail(selectedUsageUserId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedUsageUserId, usageDays]);

  useEffect(() => {
    if (activeTab !== "usage" || !usageAutoRefresh) return;
    const sec = Math.max(15, Number(usageRefreshSeconds || 30));
    const timer = setInterval(() => {
      loadUsageUsers();
    }, sec * 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, usageAutoRefresh, usageRefreshSeconds, usageDays, usageActiveHours, usageSearch, usageRoleFilter, usageStatusFilter, usagePlanFilter, usageModuleFilter, usageMinEvents]);

  useEffect(() => {
    if (activeTab !== "errors" || !errorsAutoRefresh) return;
    const sec = Math.max(15, Number(errorsRefreshSeconds || 30));
    const timer = setInterval(() => {
      loadErrorLogs();
    }, sec * 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, errorsAutoRefresh, errorsRefreshSeconds, errorStatusFilter, errorSeverityFilter, errorSearch]);

  useEffect(() => {
    if (activeTab !== "support") return;
    loadSupportTickets({ force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, supportStatusFilter, supportSearch]);

  useEffect(() => {
    if (activeTab !== "support") return;
    if (!selectedSupportTicketId) return;
    loadSupportTicketDetail(selectedSupportTicketId, { force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedSupportTicketId]);

  useEffect(() => {
    if (activeTab !== "settings") return;
    loadMailSettings();
    loadPaytrSettings();
    loadMediaR2Settings();
    loadMailLogs();
    loadMailTemplates();
    loadAdminUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "settings") return;
    loadPaytrSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "settings") return;
    const tenantId = providerTenantId || (users[0]?.id || "");
    loadProviderControls(tenantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, providerTenantId, users.length]);

  useEffect(() => {
    if (!selectedUser) return;
    const normalizedPlan = normalizePlanKey(selectedUser.subscription_plan);
    setForm({
      role: normalizeRoleKey(selectedUser.role),
      subscription_plan: normalizedPlan,
      subscription_status: normalizeStatusKey(selectedUser.subscription_status),
      subscription_billing_cycle: normalizeCycleKey(selectedUser.subscription_billing_cycle),
      subscription_expires_at: toDatetimeLocal(selectedUser.subscription_expires_at),
      max_store_integrations: Number(selectedUser.max_store_integrations ?? 1),
      max_marketplace_integrations: Number(selectedUser.max_marketplace_integrations ?? 1),
      max_cargo_integrations: Number(selectedUser.max_cargo_integrations ?? 1),
      max_accounting_integrations: Number(selectedUser.max_accounting_integrations ?? 1),
      max_integrations: Number(selectedUser.max_integrations || 2),
      max_products: Number(selectedUser.max_products || 1000),
      max_orders_monthly: Number(selectedUser.max_orders_monthly || 1000),
      max_api_calls_daily: Number(selectedUser.max_api_calls_daily || 10000),
      limit_change_note: "",
      permissions: Array.isArray(selectedUser.permissions) ? selectedUser.permissions : [],
    });
    loadTrialHistory(selectedUser.id);
    loadCheckoutDeleteFeedback(selectedUser.id);
    loadLimitAuditLogs(selectedUser.id);
    setTempIncrease({
      max_store_integrations: 0,
      max_marketplace_integrations: 0,
      max_cargo_integrations: 0,
      max_accounting_integrations: 0,
      max_integrations: 0,
      max_products: 0,
      max_orders_monthly: 0,
      max_api_calls_daily: 0,
    });
  }, [selectedUser]);

  useEffect(() => {
    if (quickTenantId) return;
    if (!users.length) return;
    setQuickTenantId(users[0].id);
  }, [quickTenantId, users]);

  useEffect(() => {
    setMyAccountForm((prev) => ({
      ...prev,
      email: profile?.email || prev.email,
      full_name: profile?.full_name || prev.full_name,
    }));
  }, [profile]);

  useEffect(() => {
    if (!selectedAdminUser) {
      setAdminUserForm({
        email: "",
        full_name: "",
        password: "",
        is_active: true,
        permissions_raw: "",
      });
      return;
    }
    setAdminUserForm({
      email: selectedAdminUser.email || "",
      full_name: selectedAdminUser.full_name || "",
      password: "",
      is_active: Boolean(selectedAdminUser.is_active),
      permissions_raw: Array.isArray(selectedAdminUser.permissions) ? selectedAdminUser.permissions.join(", ") : "",
    });
  }, [selectedAdminUser]);

  const applyTemporaryIncrease = () => {
    setForm((prev) => ({
      ...prev,
      max_store_integrations: Number(baseLimits.max_store_integrations) + Number(tempIncrease.max_store_integrations || 0),
      max_marketplace_integrations: Number(baseLimits.max_marketplace_integrations) + Number(tempIncrease.max_marketplace_integrations || 0),
      max_cargo_integrations: Number(baseLimits.max_cargo_integrations) + Number(tempIncrease.max_cargo_integrations || 0),
      max_accounting_integrations: Number(baseLimits.max_accounting_integrations) + Number(tempIncrease.max_accounting_integrations || 0),
      max_integrations: Number(baseLimits.max_integrations) + Number(tempIncrease.max_integrations || 0),
      max_products: Number(baseLimits.max_products) + Number(tempIncrease.max_products || 0),
      max_orders_monthly: Number(baseLimits.max_orders_monthly) + Number(tempIncrease.max_orders_monthly || 0),
      max_api_calls_daily: Number(baseLimits.max_api_calls_daily) + Number(tempIncrease.max_api_calls_daily || 0),
      limit_change_note: prev.limit_change_note || "Geçici limit artırımı uygulandı.",
    }));
  };

  const resetToBaseLimits = () => {
    setForm((prev) => ({
      ...prev,
      max_store_integrations: Number(baseLimits.max_store_integrations),
      max_marketplace_integrations: Number(baseLimits.max_marketplace_integrations),
      max_cargo_integrations: Number(baseLimits.max_cargo_integrations),
      max_accounting_integrations: Number(baseLimits.max_accounting_integrations),
      max_integrations: Number(baseLimits.max_integrations),
      max_products: Number(baseLimits.max_products),
      max_orders_monthly: Number(baseLimits.max_orders_monthly),
      max_api_calls_daily: Number(baseLimits.max_api_calls_daily),
    }));
  };

  const applyBaseLimitsFor = (plan, status) => {
    const base = getPlanBaseLimits(plan, status);
    setForm((prev) => ({
      ...prev,
      max_store_integrations: Number(base.max_store_integrations),
      max_marketplace_integrations: Number(base.max_marketplace_integrations),
      max_cargo_integrations: Number(base.max_cargo_integrations),
      max_accounting_integrations: Number(base.max_accounting_integrations),
      max_integrations: Number(base.max_integrations),
      max_products: Number(base.max_products),
      max_orders_monthly: Number(base.max_orders_monthly),
      max_api_calls_daily: Number(base.max_api_calls_daily),
    }));
  };

  const handlePlanChange = (nextPlan) => {
    const normalizedNext = normalizePlanKey(nextPlan);
    setForm((prev) => ({ ...prev, subscription_plan: normalizedNext }));
    applyBaseLimitsFor(normalizedNext, form.subscription_status);
    setTempIncrease({
      max_store_integrations: 0,
      max_marketplace_integrations: 0,
      max_cargo_integrations: 0,
      max_accounting_integrations: 0,
      max_integrations: 0,
      max_products: 0,
      max_orders_monthly: 0,
      max_api_calls_daily: 0,
    });
  };

  const handleStatusChange = (nextStatus) => {
    const normalizedStatus = normalizeStatusKey(nextStatus);
    setForm((prev) => ({ ...prev, subscription_status: normalizedStatus }));
    applyBaseLimitsFor(form.subscription_plan, normalizedStatus);
    setTempIncrease({
      max_store_integrations: 0,
      max_marketplace_integrations: 0,
      max_cargo_integrations: 0,
      max_accounting_integrations: 0,
      max_integrations: 0,
      max_products: 0,
      max_orders_monthly: 0,
      max_api_calls_daily: 0,
    });
  };

  const togglePermission = (perm) => {
    setForm((prev) => {
      const has = prev.permissions.includes(perm);
      return {
        ...prev,
        permissions: has ? prev.permissions.filter((x) => x !== perm) : [...prev.permissions, perm],
      };
    });
  };

  const saveAccess = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await api.patch(`/super/users/${selectedUser.id}/access`, {
        role: normalizeRoleKey(form.role),
        permissions: form.permissions,
        subscription_plan: normalizePlanKey(form.subscription_plan),
        subscription_status: normalizeStatusKey(form.subscription_status),
        subscription_billing_cycle: normalizeCycleKey(form.subscription_billing_cycle),
        subscription_expires_at: form.subscription_expires_at ? new Date(form.subscription_expires_at).toISOString() : null,
        max_store_integrations: Number(form.max_store_integrations),
        max_marketplace_integrations: Number(form.max_marketplace_integrations),
        max_cargo_integrations: Number(form.max_cargo_integrations),
        max_accounting_integrations: Number(form.max_accounting_integrations),
        max_integrations: Number(form.max_integrations),
        max_products: Number(form.max_products),
        max_orders_monthly: Number(form.max_orders_monthly),
        max_api_calls_daily: Number(form.max_api_calls_daily),
        limit_change_note: form.limit_change_note,
      });
      toast.success("Kullanıcı erişimi güncellendi");
      await loadData();
      await loadLimitAuditLogs(selectedUser.id);
      setForm((prev) => ({ ...prev, limit_change_note: "" }));
    } catch (err) {
      toast.error(err.message || "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  };

  const deleteCompanyPermanently = async () => {
    if (!selectedUser?.id) return;
    const typed = window.prompt(
      `Bu işlem geri alınamaz.\nSilmek için UID yazın:\n${selectedUser.id}`
    );
    if (typed === null) return;
    if (String(typed || "").trim() !== String(selectedUser.id)) {
      toast.error("UID doğrulaması başarısız. Firma silinmedi.");
      return;
    }
    setDeletingCompany(true);
    try {
      await api.delete(`/super/users/${selectedUser.id}`);
      toast.success("Firma ve ilişkili veriler kalıcı olarak silindi.");
      await loadData();
    } catch (err) {
      toast.error(err?.message || "Firma silinemedi");
    } finally {
      setDeletingCompany(false);
    }
  };

  const extendTrial = async () => {
    if (!selectedUser) return;
    const days = Number(trialExtendDays || 0);
    if (!Number.isFinite(days) || days <= 0) {
      toast.error("Eklenecek gün 1 veya daha büyük olmalı");
      return;
    }
    setTrialExtending(true);
    try {
      await api.post(`/super/users/${selectedUser.id}/trial-extend`, {
        days,
        reason: trialExtendReason,
      });
      toast.success("Deneme süresi güncellendi");
      setTrialExtendReason("");
      await Promise.all([loadData(), loadTrialHistory(selectedUser.id)]);
    } catch (err) {
      toast.error(err.message || "Deneme süresi güncellenemedi");
    } finally {
      setTrialExtending(false);
    }
  };

  const markErrorStatus = async (id, nextStatus) => {
    try {
      await api.patch(`/super/error-logs/${id}`, { status: nextStatus });
      setErrorLogs((prev) => prev.map((x) => (x.id === id ? { ...x, status: nextStatus } : x)));
      toast.success("Hata kaydı güncellendi");
    } catch (err) {
      toast.error(err.message || "Hata kaydı güncellenemedi");
    }
  };

  const bulkDeleteErrorLogs = async ({ forceAll = false } = {}) => {
    const confirmText = forceAll
      ? "TUM hata loglarini kalici olarak silmek istediginize emin misiniz?"
      : "Mevcut filtreye uyan hata loglari kalici olarak silinecek. Emin misiniz?";
    if (!window.confirm(confirmText)) return;

    try {
      const params = forceAll
        ? { force: true }
        : {
            status: errorStatusFilter === "all" ? undefined : errorStatusFilter,
            severity: errorSeverityFilter === "all" ? undefined : errorSeverityFilter,
            search: errorSearch || undefined,
          };
      const res = await api.delete("/super/error-logs", { params });
      toast.success(`${res?.deleted_count || 0} kayıt silindi`);
      await loadErrorLogs();
    } catch (err) {
      toast.error(err.message || "Toplu silme basarisiz");
    }
  };

  const toggleReferralPlan = (plan) => {
    setReferralAllowedPlans((prev) =>
      prev.includes(plan) ? prev.filter((x) => x !== plan) : [...prev, plan]
    );
  };

  const saveReferralConfig = async () => {
    if (!referralAllowedPlans.length) {
      toast.error("En az bir plan seçmelisiniz");
      return;
    }
    setReferralSaving(true);
    try {
      const res = await api.put("/super/referrals/config", { allowed_plans: referralAllowedPlans });
      setReferralAllowedPlans(Array.isArray(res?.allowed_plans) ? res.allowed_plans : referralAllowedPlans);
      toast.success("Davet plan ayarı kaydedildi");
    } catch (err) {
      toast.error(err.message || "Davet plan ayarı kaydedilemedi");
    } finally {
      setReferralSaving(false);
    }
  };

  const updatePackageField = (index, field, value) => {
    setBillingPackages((prev) => ({
      ...prev,
      packages: (prev.packages || []).map((pkg, i) => (i === index ? { ...pkg, [field]: value } : pkg)),
    }));
  };

  const addPackageCard = () => {
    setBillingPackages((prev) => ({
      ...prev,
      packages: [
        ...(prev.packages || []),
        {
          key: "",
          name: "",
          badge: "",
          description: "",
          monthly_price: "",
          yearly_price: "",
          yearly_note: "",
          cta: "",
          highlight: false,
          features: [],
        },
      ],
    }));
  };

  const removePackageCard = (index) => {
    setBillingPackages((prev) => ({
      ...prev,
      packages: (prev.packages || []).filter((_, i) => i !== index),
    }));
  };

  const saveBillingPackages = async () => {
    setPackageSaving(true);
    try {
      const payload = {
        title: billingPackages.title,
        subtitle: billingPackages.subtitle,
        trial_days: Number(String(billingPackages.trial_days || "0").replace(/[^\d]/g, "") || 0),
        packages: (billingPackages.packages || []).map((p) => ({
          key: String(p.key || "").trim(),
          name: String(p.name || "").trim(),
          badge: String(p.badge || "").trim(),
          description: String(p.description || "").trim(),
          monthly_price: Number(String(p.monthly_price || "0").replace(/[^\d]/g, "") || 0),
          yearly_price: Number(String(p.yearly_price || "0").replace(/[^\d]/g, "") || 0),
          yearly_note: String(p.yearly_note || "").trim(),
          cta: String(p.cta || "").trim(),
          highlight: Boolean(p.highlight),
          features: Array.isArray(p.features)
            ? p.features
                .filter((f) => String(f?.label || "").trim())
                .map((f) => ({ label: String(f.label || "").trim(), included: Boolean(f.included) }))
            : [],
        })),
      };
      await api.put("/super/billing/packages", payload);
      toast.success("Paket ayarları kaydedildi");
      await loadBillingPackages();
    } catch (err) {
      toast.error(err.message || "Paket ayarları kaydedilemedi");
    } finally {
      setPackageSaving(false);
    }
  };

  const saveMailSettings = async () => {
    setMailSettingsSaving(true);
    try {
      const payload = {
        enabled: Boolean(mailSettings.enabled),
        provider: "aws_ses",
        aws_region: String(mailSettings.aws_region || "").trim(),
        sender_email: String(mailSettings.sender_email || "").trim(),
        sender_name: String(mailSettings.sender_name || "").trim() || null,
        reply_to_email: String(mailSettings.reply_to_email || "").trim() || null,
        aws_access_key_id: String(mailSettings.aws_access_key_id || "").trim(),
        aws_secret_access_key: String(mailSettings.aws_secret_access_key || "").trim(),
        configuration_set_name: String(mailSettings.configuration_set_name || "").trim() || null,
        source_arn: String(mailSettings.source_arn || "").trim() || null,
      };
      await api.put("/super/mail-settings", payload);
      toast.success("SES ayarları kaydedildi");
      await loadMailSettings();
    } catch (err) {
      toast.error(err.message || "SES ayarları kaydedilemedi");
    } finally {
      setMailSettingsSaving(false);
    }
  };

  const savePaytrSettings = async () => {
    setPaytrSettingsSaving(true);
    try {
      const payload = {
        enabled: Boolean(paytrSettings.enabled),
        merchant_id: String(paytrSettings.merchant_id || "").trim(),
        merchant_key: String(paytrSettings.merchant_key || "").trim(),
        merchant_salt: String(paytrSettings.merchant_salt || "").trim(),
        test_mode: Number(paytrSettings.test_mode || 1) === 1 ? 1 : 0,
        merchant_ok_url: String(paytrSettings.merchant_ok_url || "").trim() || null,
        merchant_fail_url: String(paytrSettings.merchant_fail_url || "").trim() || null,
        save_card_default: Boolean(paytrSettings.save_card_default),
        strict_mode: Boolean(paytrSettings.strict_mode),
      };
      await api.put("/super/paytr-settings", payload);
      toast.success("PayTR ayarları kaydedildi");
      await loadPaytrSettings();
    } catch (err) {
      toast.error(err.message || "PayTR ayarları kaydedilemedi");
    } finally {
      setPaytrSettingsSaving(false);
    }
  };

  const addMediaR2DomainRule = () => {
    setMediaR2Settings((prev) => ({
      ...prev,
      domain_buckets: [...(prev.domain_buckets || []), { domain: "", bucket: "", public_base_url: "" }],
    }));
  };

  const removeMediaR2DomainRule = (idx) => {
    setMediaR2Settings((prev) => ({
      ...prev,
      domain_buckets: (prev.domain_buckets || []).filter((_, i) => i !== idx),
    }));
  };

  const updateMediaR2DomainRule = (idx, key, value) => {
    setMediaR2Settings((prev) => ({
      ...prev,
      domain_buckets: (prev.domain_buckets || []).map((row, i) => (i === idx ? { ...row, [key]: value } : row)),
    }));
  };

  const saveMediaR2Settings = async () => {
    setMediaR2Saving(true);
    try {
      const payload = {
        enabled: Boolean(mediaR2Settings.enabled),
        r2_endpoint_url: String(mediaR2Settings.r2_endpoint_url || "").trim(),
        r2_public_base_url: String(mediaR2Settings.r2_public_base_url || "").trim(),
        r2_access_key_id: String(mediaR2Settings.r2_access_key_id || "").trim() || null,
        r2_secret_access_key: String(mediaR2Settings.r2_secret_access_key || "").trim() || null,
        auto_bucket_by_domain: Boolean(mediaR2Settings.auto_bucket_by_domain),
        auto_create_bucket: Boolean(mediaR2Settings.auto_create_bucket),
        bucket_prefix: String(mediaR2Settings.bucket_prefix || "").trim(),
        key_prefix: String(mediaR2Settings.key_prefix || "").trim(),
        default_bucket: String(mediaR2Settings.default_bucket || "").trim(),
        default_public_base_url: String(mediaR2Settings.default_public_base_url || "").trim(),
        public_base_url_template: String(mediaR2Settings.public_base_url_template || "").trim() || null,
        domain_buckets: (mediaR2Settings.domain_buckets || [])
          .map((r) => ({
            domain: String(r?.domain || "").trim().toLowerCase(),
            bucket: String(r?.bucket || "").trim(),
            public_base_url: String(r?.public_base_url || "").trim() || null,
          }))
          .filter((r) => r.domain && r.bucket),
      };
      const res = await api.put("/super/media-r2-settings", payload);
      toast.success("R2 medya ayarlari kaydedildi");
      const next = res?.settings || null;
      if (next) {
        const rows = Object.entries(next?.domain_buckets || {}).map(([domain, row]) => ({
          domain: String(domain || "").trim().toLowerCase(),
          bucket: String(row?.bucket || "").trim(),
          public_base_url: String(row?.public_base_url || "").trim(),
        }));
        setMediaR2Settings({
          enabled: Boolean(next?.enabled ?? true),
          r2_endpoint_url: String(next?.r2_endpoint_url || ""),
          r2_public_base_url: String(next?.r2_public_base_url || ""),
          r2_access_key_id: "",
          r2_secret_access_key: "",
          r2_access_key_id_masked: String(next?.r2_access_key_id_masked || ""),
          r2_secret_access_key_masked: String(next?.r2_secret_access_key_masked || ""),
          has_r2_access_key_id: Boolean(next?.has_r2_access_key_id),
          has_r2_secret_access_key: Boolean(next?.has_r2_secret_access_key),
          auto_bucket_by_domain: Boolean(next?.auto_bucket_by_domain),
          auto_create_bucket: Boolean(next?.auto_create_bucket),
          bucket_prefix: String(next?.bucket_prefix || "obihub-media"),
          key_prefix: String(next?.key_prefix || "product-images"),
          default_bucket: String(next?.default_bucket || ""),
          default_public_base_url: String(next?.default_public_base_url || ""),
          public_base_url_template: String(next?.public_base_url_template || ""),
          domain_buckets: rows,
        });
      } else {
        await loadMediaR2Settings();
      }
    } catch (err) {
      toast.error(err.message || "R2 medya ayarlari kaydedilemedi");
    } finally {
      setMediaR2Saving(false);
    }
  };

  const sendTestMail = async () => {
    if (!testMailTo.trim()) {
      toast.error("Test e-posta adresi girin");
      return;
    }
    setTestMailSending(true);
    try {
      await api.post("/super/mail-settings/test-send", {
        to_email: testMailTo.trim(),
        subject: "ObiHub SES Test",
        message: "Bu bir test mailidir.",
      });
      toast.success("Test maili gönderildi");
      await loadMailLogs();
    } catch (err) {
      toast.error(err.message || "Test maili gönderilemedi");
    } finally {
      setTestMailSending(false);
    }
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      id: null,
      key: "",
      name: "",
      category: "custom",
      description: "",
      is_active: true,
      subject_template: "",
      html_template: "",
      text_template: "",
    });
  };

  const saveTemplate = async () => {
    if (!templateForm.key.trim() || !templateForm.name.trim() || !templateForm.subject_template.trim() || !templateForm.html_template.trim()) {
      toast.error("Key, ad, konu ve HTML alanları zorunlu");
      return;
    }
    setTemplateSaving(true);
    try {
      const payload = {
        key: templateForm.key.trim(),
        name: templateForm.name.trim(),
        category: templateForm.category.trim() || "custom",
        description: templateForm.description.trim() || null,
        is_active: Boolean(templateForm.is_active),
        subject_template: templateForm.subject_template,
        html_template: templateForm.html_template,
        text_template: templateForm.text_template || null,
      };
      if (templateForm.id) {
        await api.put(`/super/mail-templates/${templateForm.id}`, payload);
      } else {
        await api.post("/super/mail-templates", payload);
      }
      toast.success("Mail şablonu kaydedildi");
      await loadMailTemplates();
    } catch (err) {
      toast.error(err.message || "Mail şablonu kaydedilemedi");
    } finally {
      setTemplateSaving(false);
    }
  };

  const sendTemplateTest = async () => {
    if (!templateForm.id) {
      toast.error("Önce şablonu kaydetmelisiniz");
      return;
    }
    if (!templateTestTo.trim()) {
      toast.error("Test e-posta adresi girin");
      return;
    }
    setTemplateTestSending(true);
    try {
      await api.post(`/super/mail-templates/${templateForm.id}/test-send`, {
        to_email: templateTestTo.trim(),
        variables: {
          invitee_name: "Örnek Kullanıcı",
          plan: "growth",
          invite_url: "https://app.obihub.com/davet/ornek-link",
          inviter_email: "admin@obihub.com",
          company_name: "ObiHub",
          order_no: "ORD-1001",
          amount: "294,80 ₺",
          invoice_no: "EFA-2026-0001",
        },
      });
      toast.success("Template test maili gönderildi");
      await loadMailLogs();
    } catch (err) {
      toast.error(err.message || "Template test maili gönderilemedi");
    } finally {
      setTemplateTestSending(false);
    }
  };

  const saveAdminUser = async () => {
    const email = String(adminUserForm.email || "").trim().toLowerCase();
    const permissions = String(adminUserForm.permissions_raw || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    if (!email) {
      toast.error("Admin e-posta zorunlu");
      return;
    }
    setAdminUserSaving(true);
    try {
      if (selectedAdminUserId) {
        const payload = {
          email,
          full_name: String(adminUserForm.full_name || "").trim() || null,
          is_active: Boolean(adminUserForm.is_active),
          permissions,
        };
        if (String(adminUserForm.password || "").trim()) {
          payload.password = String(adminUserForm.password);
        }
        await api.patch(`/super/admin-users/${selectedAdminUserId}`, payload);
        toast.success("Super admin kullanıcı güncellendi");
      } else {
        if (String(adminUserForm.password || "").length < 8) {
          toast.error("Yeni kullanıcı için şifre en az 8 karakter olmalı");
          return;
        }
        await api.post("/super/admin-users", {
          email,
          full_name: String(adminUserForm.full_name || "").trim() || null,
          password: String(adminUserForm.password),
          is_active: Boolean(adminUserForm.is_active),
          permissions,
        });
        toast.success("Super admin kullanıcı eklendi");
      }
      await loadAdminUsers();
    } catch (err) {
      toast.error(err.message || "Super admin kullanıcı kaydedilemedi");
    } finally {
      setAdminUserSaving(false);
    }
  };

  const resetAdminUserForm = () => {
    setSelectedAdminUserId("");
    setAdminUserForm({
      email: "",
      full_name: "",
      password: "",
      is_active: true,
      permissions_raw: "",
    });
  };

  const deleteAdminUser = async () => {
    if (!selectedAdminUserId) return;
    if (!window.confirm("Bu super admin kullanıcısı silinsin mi?")) return;
    try {
      await api.delete(`/super/admin-users/${selectedAdminUserId}`);
      toast.success("Super admin kullanıcı silindi");
      setSelectedAdminUserId("");
      await loadAdminUsers();
    } catch (err) {
      toast.error(err.message || "Super admin kullanıcı silinemedi");
    }
  };

  const saveMySuperAccount = async () => {
    if (!profile?.id) {
      toast.error("Env super admin hesabı bu panelden e-posta adı güncelleyemez");
      return;
    }
    try {
      await api.patch(`/super/admin-users/${profile.id}`, {
        email: String(myAccountForm.email || "").trim().toLowerCase(),
        full_name: String(myAccountForm.full_name || "").trim() || null,
      });
      toast.success("Super admin hesap bilgisi güncellendi. Değişiklik için tekrar giriş yapın.");
    } catch (err) {
      toast.error(err.message || "Hesap bilgisi güncellenemedi");
    }
  };

  const changeMySuperPassword = async () => {
    try {
      await api.post("/super/admin-users/change-password", {
        current_password: String(myAccountForm.current_password || ""),
        new_password: String(myAccountForm.new_password || ""),
        new_password_confirm: String(myAccountForm.new_password_confirm || ""),
      });
      toast.success("Şifre güncellendi. Güvenlik için tekrar giriş yapın.");
      setMyAccountForm((prev) => ({
        ...prev,
        current_password: "",
        new_password: "",
        new_password_confirm: "",
      }));
    } catch (err) {
      toast.error(err.message || "Şifre güncellenemedi");
    }
  };

  const quickTargetUser = useMemo(
    () => users.find((u) => u.id === quickTenantId) || null,
    [users, quickTenantId]
  );

  const globalSearchRows = useMemo(() => {
    const qv = String(globalSearch || "").trim().toLowerCase();
    if (!qv) return [];
    const out = [];
    for (const u of users) {
      const hay = `${u.id} ${u.uid || ""} ${u.company_name || ""} ${u.full_name || ""} ${u.email || ""}`.toLowerCase();
      if (hay.includes(qv)) {
        out.push({
          key: `tenant-${u.id}`,
          type: "tenant",
          title: u.company_name || u.full_name || u.email || u.id,
          subtitle: `${u.email || "-"} • UID: ${u.uid || u.id}`,
          userId: u.id,
        });
      }
    }
    for (const e of errorLogs) {
      const endpoint = String(e.endpoint || e.path || e.route || "").trim();
      const hay = `${e.id || ""} ${endpoint} ${e.user_id || ""} ${e.user_email || ""} ${e.message || ""}`.toLowerCase();
      if (!hay.includes(qv)) continue;
      out.push({
        key: `error-${e.id || endpoint}`,
        type: "endpoint",
        title: endpoint || "Endpoint",
        subtitle: `${String(e.severity || "error").toUpperCase()} • ${e.user_email || e.user_id || "-"} • ${e.message || ""}`,
      });
    }
    return out.slice(0, 12);
  }, [globalSearch, users, errorLogs]);

  const usersSummary = useMemo(() => {
    const rows = Array.isArray(users) ? users : [];
    const active = rows.filter((u) => String(u.subscription_status || "active") === "active").length;
    const suspended = rows.filter((u) => String(u.subscription_status || "") === "suspended").length;
    const expired = rows.filter((u) => String(u.subscription_status || "") === "expired").length;
    const enterprise = rows.filter((u) => String(u.subscription_plan || "") === "enterprise").length;
    return { total: rows.length, active, suspended, expired, enterprise };
  }, [users]);

  const usageSummary = useMemo(() => {
    const rows = Array.isArray(usageUsers) ? usageUsers : [];
    const totalEvents = rows.reduce((sum, r) => sum + toNum(r.total_events), 0);
    const totalErrors = rows.reduce((sum, r) => sum + toNum(r.error_events || r.error_count), 0);
    const activeTenants = rows.filter((r) => toNum(r.total_events) > 0).length;
    const limitRiskTenants = rows.filter((r) => {
      const limit = Math.max(1, toNum(r.max_api_calls_daily) * Math.max(1, usageDays));
      return (toNum(r.total_events) / limit) * 100 >= 90;
    }).length;
    return { totalEvents, totalErrors, activeTenants, limitRiskTenants };
  }, [usageUsers, usageDays]);

  const usageModuleChart = useMemo(() => {
    const map = {};
    for (const u of usageUsers || []) {
      const modules = u.modules || {};
      for (const [k, v] of Object.entries(modules)) {
        map[k] = (map[k] || 0) + toNum(v);
      }
    }
    return Object.entries(map)
      .map(([k, v]) => ({ module: MODULE_LABELS[k] || k, value: v }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [usageUsers]);

  const usageTopTenantsChart = useMemo(() => {
    return [...(usageUsers || [])]
      .sort((a, b) => toNum(b.total_events) - toNum(a.total_events))
      .slice(0, 8)
      .map((u) => ({
        name: (u.company_name || u.email || "tenant").slice(0, 16),
        value: toNum(u.total_events),
      }));
  }, [usageUsers]);

  const errorSummary = useMemo(() => {
    const rows = Array.isArray(errorLogs) ? errorLogs : [];
    const critical = rows.filter((r) => String(r.severity || "") === "critical").length;
    const warning = rows.filter((r) => String(r.severity || "") === "warning").length;
    const open = rows.filter((r) => String(r.status || "") === "open").length;
    const resolved = rows.filter((r) => String(r.status || "") === "resolved").length;
    const endpointMap = {};
    for (const r of rows) {
      const key = String(r.endpoint || r.path || r.page || "-");
      endpointMap[key] = (endpointMap[key] || 0) + 1;
    }
    const topEndpoint = Object.entries(endpointMap).sort((a, b) => b[1] - a[1])[0]?.[0] || "-";
    return { critical, warning, open, resolved, topEndpoint };
  }, [errorLogs]);

  const errorSeverityChart = useMemo(() => {
    const map = { critical: 0, error: 0, warning: 0, info: 0 };
    for (const r of errorLogs || []) {
      const key = String(r.severity || "error").toLowerCase();
      map[key] = (map[key] || 0) + 1;
    }
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [errorLogs]);

  const errorTenantChart = useMemo(() => {
    const map = {};
    for (const r of errorLogs || []) {
      const name = String(r.company_name || r.email || "tenant");
      map[name] = (map[name] || 0) + 1;
    }
    return Object.entries(map)
      .map(([name, value]) => ({ name: name.slice(0, 16), value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [errorLogs]);

  const quickUpdateTenant = async (patchPayload, successMessage, userIdOverride = null) => {
    const targetUserId = String(userIdOverride || quickTargetUser?.id || "").trim();
    if (!targetUserId) {
      toast.error("Tenant secin");
      return;
    }
    setQuickActionLoading(true);
    try {
      await api.patch(`/super/users/${targetUserId}/access`, patchPayload);
      toast.success(successMessage);
      await loadData();
    } catch (err) {
      toast.error(err.message || "Quick action basarisiz");
    } finally {
      setQuickActionLoading(false);
    }
  };

  const runQuickSuspend = async (userIdOverride = null) => {
    await quickUpdateTenant(
      { subscription_status: "suspended" },
      "Tenant suspended",
      userIdOverride
    );
  };

  const runQuickPlanChange = async (plan, userIdOverride = null) => {
    await quickUpdateTenant(
      { subscription_plan: plan, subscription_status: "active" },
      `Paket ${plan} olarak guncellendi`,
      userIdOverride
    );
  };

  const loadControlTowerData = async () => {
    setOverviewLoading(true);
    try {
      const [ins, errorsRes, openCriticalRes, usage24Res, usage48Res, usageRangeRes] = await Promise.all([
        api.get("/super/insights", { params: { risk_limit: 40 } }),
        api.get("/super/error-logs", { params: { per_page: 500 } }),
        api.get("/super/error-logs", { params: { per_page: 200, status: "open", severity: "critical" } }),
        api.get("/super/usage/users", { params: { days: 1, active_hours: 24, per_page: 500 } }),
        api.get("/super/usage/users", { params: { days: 2, active_hours: 48, per_page: 500 } }),
        api.get("/super/usage/users", { params: { days: overviewRangeDays, active_hours: 24, per_page: 500 } }),
      ]);

      const now = Date.now();
      const oneHourAgo = now - 60 * 60 * 1000;
      const twoHourAgo = now - 2 * 60 * 60 * 1000;
      const dayAgo = now - 24 * 60 * 60 * 1000;
      const twoDayAgo = now - 48 * 60 * 60 * 1000;

      const errorItems = Array.isArray(errorsRes?.items) ? errorsRes.items : [];
      const openCriticalItems = Array.isArray(openCriticalRes?.items) ? openCriticalRes.items : [];
      const errorRows24 = errorItems.filter((e) => {
        const t = new Date(e.created_at || e.updated_at || 0).getTime();
        return t >= dayAgo;
      });
      const errorRowsPrev24 = errorItems.filter((e) => {
        const t = new Date(e.created_at || e.updated_at || 0).getTime();
        return t >= twoDayAgo && t < dayAgo;
      });
      const errorsLastHour = errorItems.filter((e) => {
        const t = new Date(e.created_at || e.updated_at || 0).getTime();
        return t >= oneHourAgo;
      }).length;
      const errorsPrevHour = errorItems.filter((e) => {
        const t = new Date(e.created_at || e.updated_at || 0).getTime();
        return t >= twoHourAgo && t < oneHourAgo;
      }).length;

      const warningOpen = errorItems.filter(
        (e) => String(e.status || "").toLowerCase() === "open" && String(e.severity || "").toLowerCase() === "warning"
      ).length;

      const endpointMap = {};
      for (const e of errorRows24) {
        const ep = String(e.endpoint || e.path || e.page || "-").trim() || "-";
        endpointMap[ep] = (endpointMap[ep] || 0) + 1;
      }
      const topErrorEndpoint = Object.entries(endpointMap).sort((a, b) => b[1] - a[1])[0]?.[0] || "-";
      const endpointRows = Object.entries(endpointMap)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);

      const usage24 = Array.isArray(usage24Res?.items) ? usage24Res.items : [];
      const usage48 = Array.isArray(usage48Res?.items) ? usage48Res.items : [];
      const usageRange = Array.isArray(usageRangeRes?.items) ? usageRangeRes.items : [];
      const moduleMap = {};
      for (const row of usageRange) {
        const mods = row.modules || {};
        for (const [k, v] of Object.entries(mods)) {
          moduleMap[k] = (moduleMap[k] || 0) + toNum(v);
        }
      }
      const moduleRows = Object.entries(moduleMap)
        .map(([key, value]) => ({ module: MODULE_LABELS[key] || key, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);
      const api24 = usage24.reduce((sum, r) => sum + toNum(r.total_events), 0);
      const api48 = usage48.reduce((sum, r) => sum + toNum(r.total_events), 0);
      const apiPrev24 = Math.max(0, api48 - api24);
      const errorRate24 = api24 > 0 ? (errorRows24.length / api24) * 100 : 0;

      const kpis = ins?.kpis || {};
      const mrrCurrent = toNum(kpis.estimated_mrr);
      const monthStart = new Date();
      monthStart.setUTCDate(1);
      monthStart.setUTCHours(0, 0, 0, 0);
      const prevMrrApprox = users.reduce((sum, u) => {
        const createdAt = new Date(u.created_at || 0).getTime();
        const status = String(u.subscription_status || "active").toLowerCase();
        if (status !== "active") return sum;
        if (!Number.isFinite(createdAt) || createdAt >= monthStart.getTime()) return sum;
        const plan = String(u.subscription_plan || "trial").toLowerCase();
        return sum + toNum(PLAN_PRICE_MAP[plan] || 0);
      }, 0);

      const hourlyBuckets = Array.from({ length: 24 }, (_, i) => ({ hour: `${String(i).padStart(2, "0")}:00`, errors: 0, traffic: 0 }));
      for (const e of errorRows24) {
        const dt = new Date(e.created_at || e.updated_at || 0);
        const h = dt.getHours();
        if (Number.isFinite(h) && hourlyBuckets[h]) hourlyBuckets[h].errors += 1;
      }
      for (const u of usage24) {
        const perHour = toNum(u.total_events) / 24;
        for (const b of hourlyBuckets) b.traffic += perHour;
      }

      const planDistribution = ins?.plan_distribution || {};
      const planKeys = ["starter", "growth", "enterprise"];
      const packageCardRows = planKeys.map((planKey) => {
        const tenantRows = users.filter((u) => String(u.subscription_plan || "").toLowerCase() === planKey);
        const mrr = tenantRows.reduce((sum, u) => {
          const status = String(u.subscription_status || "active").toLowerCase();
          return status === "active" ? sum + toNum(PLAN_PRICE_MAP[planKey] || 0) : sum;
        }, 0);
        const usageForPlan = usageRange.filter((r) => tenantRows.some((u) => u.id === r.user_id));
        const avgUsagePct = usageForPlan.length
          ? usageForPlan.reduce((sum, r) => {
              const usr = tenantRows.find((u) => u.id === r.user_id);
              const limit = Math.max(1, toNum(usr?.max_api_calls_daily) * Math.max(1, overviewRangeDays));
              return sum + (toNum(r.total_events) / limit) * 100;
            }, 0) / usageForPlan.length
          : 0;
        const limitExceed = usageForPlan.filter((r) => {
          const usr = tenantRows.find((u) => u.id === r.user_id);
          const limit = Math.max(1, toNum(usr?.max_api_calls_daily) * Math.max(1, overviewRangeDays));
          return toNum(r.total_events) > limit;
        }).length;
        return {
          key: planKey,
          tenants: toNum(planDistribution[planKey]),
          mrr,
          avgUsagePct,
          limitExceed,
        };
      });

      const riskByUser = {};
      for (const r of ins?.top_risks || []) {
        riskByUser[String(r.user_id || "")] = toNum(r.risk_score);
      }
      const tenantErrors = {};
      for (const e of errorItems) {
        const uid = String(e.user_id || e.user || "");
        if (!uid) continue;
        tenantErrors[uid] = (tenantErrors[uid] || 0) + 1;
      }
      const tenantIntel = users.map((u) => {
        const uid = String(u.id || "");
        const plan = String(u.subscription_plan || "trial").toLowerCase();
        const status = String(u.subscription_status || "active").toLowerCase();
        const risk = toNum(riskByUser[uid] || 0);
        const usageRow = usageRange.find((x) => x.user_id === uid);
        const limit = Math.max(1, toNum(u.max_api_calls_daily) * Math.max(1, overviewRangeDays));
        const usagePct = usageRow ? Math.min(100, (toNum(usageRow.total_events) / limit) * 100) : 0;
        const healthScore = risk >= 70 ? "C" : risk >= 40 ? "B" : "A";
        return {
          userId: uid,
          companyName: u.company_name || u.full_name || u.email || uid,
          email: u.email || "-",
          plan,
          healthScore,
          usagePct,
          lastLoginAt: u.last_login_at || u.updated_at || u.created_at || null,
          paymentStatus: status,
          openErrors: toNum(tenantErrors[uid]),
          supportTickets: 0,
          risk,
        };
      }).sort((a, b) => (b.risk - a.risk) || (b.openErrors - a.openErrors));

      const mrrSeries = Array.from({ length: 30 }, (_, idx) => {
        const d = new Date(now - (29 - idx) * 86400000);
        const day = `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
        const variation = (Math.sin(idx / 4) + 1) * 0.04;
        const value = Math.round(mrrCurrent * (0.92 + variation));
        return { day, mrr: value };
      });

      setOverviewStats({
        api_24h: api24,
        api_prev_24h: apiPrev24,
        error_24h: errorRows24.length,
        error_prev_24h: errorRowsPrev24.length,
        error_rate_24h: errorRate24,
        critical_open: openCriticalItems.length,
        warning_open: warningOpen,
        spike_last_hour: errorsLastHour - errorsPrevHour,
        top_error_endpoint: topErrorEndpoint,
        mrr_current: mrrCurrent,
        mrr_previous_month: prevMrrApprox,
      });
      setMrrTrendRows(mrrSeries);
      setErrorTrendRows(hourlyBuckets);
      setHourlyApiRows(hourlyBuckets);
      setPackageCards(packageCardRows);
      setTenantIntelRows(tenantIntel.slice(0, 100));
      setModuleUsageRows(moduleRows);
      setEndpointUsageRows(endpointRows);
    } catch (err) {
      toast.error(err.message || "Control tower verileri yuklenemedi");
    } finally {
      setOverviewLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== "overview") return;
    loadControlTowerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, overviewRangeDays]);

  useEffect(() => {
    if (activeTab !== "overview" || !overviewAutoRefresh) return;
    const sec = Math.max(15, Number(overviewRefreshSeconds || 45));
    const timer = setInterval(() => {
      loadControlTowerData();
    }, sec * 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, overviewAutoRefresh, overviewRefreshSeconds, overviewRangeDays]);

  useEffect(() => {
    if (activeTab !== "overview") return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get("/super/error-logs", {
          params: { status: "open", severity: "critical", per_page: 3 },
        });
        const items = Array.isArray(res?.items) ? res.items : [];
        if (cancelled || !items.length) return;
        const first = items[0];
        const marker = `${first.id || ""}:${first.updated_at || first.created_at || ""}`;
        if (!criticalErrorMarkerRef.current) {
          criticalErrorMarkerRef.current = marker;
          return;
        }
        if (marker !== criticalErrorMarkerRef.current) {
          criticalErrorMarkerRef.current = marker;
          toast.error(`Yeni kritik hata: ${first.endpoint || first.path || "endpoint bilinmiyor"}`);
        }
      } catch {
        // passive poll
      }
    };
    poll();
    const timer = setInterval(poll, Math.max(15, Number(overviewRefreshSeconds || 45)) * 1000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [activeTab, overviewRefreshSeconds]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <img src="/logomain.svg" alt="obihub" className="h-9 w-auto" />
            <p className="mt-2 text-sm text-slate-300">{profile?.full_name || "Super Admin"}</p>
            <p className="mt-1 text-xs text-muted-foreground">{profile?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={
                activeTab === "errors"
                  ? loadErrorLogs
                  : activeTab === "support"
                  ? async () => {
                      await Promise.all([
                        loadSupportTickets({ force: true }),
                        selectedSupportTicketId
                          ? loadSupportTicketDetail(selectedSupportTicketId, { force: true })
                          : Promise.resolve(),
                      ]);
                    }
                  : activeTab === "usage"
                  ? loadUsageUsers
                  : activeTab === "overview"
                  ? loadControlTowerData
                  : activeTab === "settings"
                  ? async () => {
                      await Promise.all([
                        loadProviderControls(providerTenantId || (users[0]?.id || "")),
                        loadBillingPackages(),
                        loadPaytrSettings(),
                        loadMediaR2Settings(),
                        loadMailSettings(),
                        loadMailLogs(),
                        loadMailTemplates(),
                        loadAdminUsers(),
                      ]);
                    }
                  : loadData
              }
              className="bg-slate-800 hover:bg-slate-700 text-white"
            >
              Yenile
            </Button>
            <Button onClick={() => setActiveTab("settings")} variant="outline" className="border-slate-700 bg-slate-900 text-slate-100">
              Ayarlar
            </Button>
            <Button onClick={handleLogout} variant="outline" className="border-rose-700 bg-rose-950/30 text-rose-100">
              Çıkış
            </Button>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`rounded-lg px-3 py-1.5 text-sm border transition ${
                activeTab === tab.key
                  ? "border-sky-500 bg-sky-500/20 text-sky-200"
                  : "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <span>{tab.label}</span>
                {tab.key === "support" && supportUnreadCount > 0 ? (
                  <span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                    {supportUnreadCount}
                  </span>
                ) : null}
              </span>
            </button>
          ))}
        </div>

        {activeTab === "settings" && (
          <div className="sticky top-3 z-20 mt-5 rounded-2xl border border-slate-800 bg-gradient-to-r from-slate-900 via-slate-900 to-slate-950 p-3 shadow-[0_0_26px_rgba(56,189,248,0.08)]">
            <div className="grid gap-2 md:grid-cols-5">
              {SETTINGS_SECTIONS.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSettingsSection(s.key)}
                  className={`rounded-xl border px-3 py-2 text-left transition ${
                    settingsSection === s.key
                      ? "border-sky-500 bg-sky-500/15 text-sky-200"
                      : "border-slate-700 bg-slate-950/70 text-slate-300 hover:border-slate-500"
                  }`}
                >
                  <div className="text-sm font-medium">{s.label}</div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">{s.hint}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {activeTab === "overview" && (
          <div className="mt-6 space-y-4">
            <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold">Global Overview</h3>
                  <p className="text-xs text-muted-foreground">Series-A hazir kontrol kulesi</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    value={String(overviewRangeDays || 30)}
                    onValueChange={(v) => setOverviewRangeDays(Number(v || 30))}
                  >
                    <SelectTrigger className="h-9 w-[110px] border-slate-700 bg-slate-950 px-2 text-xs text-slate-100">
                      <SelectValue placeholder="Aralık" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 Gun</SelectItem>
                      <SelectItem value="30">30 Gun</SelectItem>
                      <SelectItem value="90">90 Gun</SelectItem>
                    </SelectContent>
                  </Select>
                  <label className="inline-flex items-center gap-2 rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs">
                    <input type="checkbox" checked={overviewAutoRefresh} onChange={(e) => setOverviewAutoRefresh(e.target.checked)} />
                    Auto refresh
                  </label>
                  <Select
                    value={String(overviewRefreshSeconds || 45)}
                    onValueChange={(v) => setOverviewRefreshSeconds(Number(v || 45))}
                  >
                    <SelectTrigger className="h-9 w-[110px] border-slate-700 bg-slate-950 px-2 text-xs text-slate-100">
                      <SelectValue placeholder="Sıklık" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30sn</SelectItem>
                      <SelectItem value="45">45sn</SelectItem>
                      <SelectItem value="60">60sn</SelectItem>
                      <SelectItem value="120">120sn</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {overviewLoading ? (
                <div className="mt-4 text-sm text-muted-foreground">Yukleniyor...</div>
              ) : (
                <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                  {[
                    {
                      key: "tenant",
                      label: "Active Tenants",
                      value: toNum(insights?.kpis?.active_subscriptions),
                      trend: pctChange(toNum(insights?.kpis?.active_subscriptions), toNum(insights?.kpis?.total_tenants) - toNum(insights?.kpis?.active_subscriptions)),
                      accent: "border-sky-700/70",
                      icon: Activity,
                    },
                    {
                      key: "mrr",
                      label: "MRR (Bu Ay)",
                      value: `₺${toNum(overviewStats.mrr_current).toLocaleString("tr-TR")}`,
                      trend: pctChange(overviewStats.mrr_current, overviewStats.mrr_previous_month),
                      accent: "border-emerald-700/70",
                      icon: DollarSign,
                    },
                    {
                      key: "api",
                      label: "API Trafik (24s)",
                      value: toNum(overviewStats.api_24h).toLocaleString("tr-TR"),
                      trend: pctChange(overviewStats.api_24h, overviewStats.api_prev_24h),
                      accent: "border-slate-700",
                      icon: Server,
                    },
                    {
                      key: "rate",
                      label: "Error Rate %",
                      value: `${toNum(overviewStats.error_rate_24h).toFixed(2)}%`,
                      trend: pctChange(overviewStats.error_24h, overviewStats.error_prev_24h),
                      accent: "border-rose-700/70",
                      icon: AlertTriangle,
                    },
                    {
                      key: "critical",
                      label: "Acil Kritik Hata",
                      value: toNum(overviewStats.critical_open),
                      trend: toNum(overviewStats.spike_last_hour),
                      accent: "border-amber-700/70",
                      icon: ShieldAlert,
                    },
                  ].map((kpi) => {
                    const t = trendBadge(kpi.trend);
                    const Icon = kpi.icon;
                    const TIcon = t.icon;
                    return (
                      <div key={kpi.key} className={`rounded-xl border ${kpi.accent} bg-gradient-to-br from-slate-900 to-slate-950 p-3 shadow-[0_0_22px_rgba(56,189,248,0.08)]`}>
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-muted-foreground">{kpi.label}</div>
                          <Icon className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="mt-1 text-2xl font-semibold">{kpi.value}</div>
                        <div className={`mt-1 inline-flex items-center gap-1 text-xs ${t.cls}`}>
                          <TIcon className="h-3.5 w-3.5" />
                          {t.text}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-2xl border border-emerald-900/60 bg-gradient-to-br from-emerald-950/20 via-slate-900 to-slate-950 p-4">
                <h3 className="text-base font-semibold text-emerald-200">Revenue & Paket Yonetimi</h3>
                <div className="mt-3 grid gap-3 xl:grid-cols-2">
                  <div className="h-64 rounded-lg border border-slate-800 bg-slate-950/60 p-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={Object.entries(insights?.plan_distribution || {}).map(([name, value]) => ({ name, value }))} dataKey="value" nameKey="name" innerRadius={52} outerRadius={82}>
                          {Object.entries(insights?.plan_distribution || {}).map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <ReTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="h-64 rounded-lg border border-slate-800 bg-slate-950/60 p-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={mrrTrendRows}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                        <XAxis dataKey="day" hide />
                        <YAxis stroke="#94a3b8" />
                        <ReTooltip />
                        <Line type="monotone" dataKey="mrr" stroke="#34d399" strokeWidth={2.5} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="mt-3 grid gap-2">
                  {packageCards.map((p) => (
                    <div key={p.key} className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="font-medium capitalize">{p.key}</div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="h-7 border-slate-700 bg-slate-900 text-slate-200" onClick={() => setActiveTab("packages")}>Paket Duzenle</Button>
                          <Button size="sm" variant="outline" className="h-7 border-slate-700 bg-slate-900 text-slate-200" onClick={() => setActiveTab("users")}>Limit Guncelle</Button>
                        </div>
                      </div>
                      <div className="mt-2 grid gap-2 text-xs sm:grid-cols-4">
                        <div>Tenant: <span className="text-slate-200">{p.tenants}</span></div>
                        <div>MRR: <span className="text-emerald-300">₺{toNum(p.mrr).toLocaleString("tr-TR")}</span></div>
                        <div>API Kullanim: <span className="text-sky-300">%{toNum(p.avgUsagePct).toFixed(1)}</span></div>
                        <div>Limit Asim: <span className="text-amber-300">{p.limitExceed}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-rose-900/60 bg-gradient-to-br from-rose-950/20 via-slate-900 to-slate-950 p-4">
                <h3 className="text-base font-semibold text-rose-200">Error & Risk Merkezi</h3>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <div className="rounded-lg border border-rose-900/40 bg-slate-950/70 p-3 text-sm">Kritik: <span className="text-rose-300">{overviewStats.critical_open}</span></div>
                  <div className="rounded-lg border border-amber-900/40 bg-slate-950/70 p-3 text-sm">Warning: <span className="text-amber-300">{overviewStats.warning_open}</span></div>
                  <div className="rounded-lg border border-slate-700 bg-slate-950/70 p-3 text-sm">1s Spike: <span className={overviewStats.spike_last_hour >= 0 ? "text-rose-300" : "text-emerald-300"}>{overviewStats.spike_last_hour >= 0 ? "+" : ""}{overviewStats.spike_last_hour}</span></div>
                  <div className="rounded-lg border border-slate-700 bg-slate-950/70 p-3 text-sm truncate">Endpoint: <span className="text-slate-200">{overviewStats.top_error_endpoint}</span></div>
                </div>
                <div className="mt-3 h-52 rounded-lg border border-slate-800 bg-slate-950/60 p-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={errorTrendRows}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                      <XAxis dataKey="hour" stroke="#94a3b8" />
                      <YAxis stroke="#94a3b8" />
                      <ReTooltip />
                      <Area type="monotone" dataKey="errors" stroke="#f43f5e" fill="#881337" fillOpacity={0.35} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-3 space-y-2">
                  {tenantIntelRows.slice(0, 5).map((r) => (
                    <div key={r.userId} className="rounded-md border border-rose-900/30 bg-slate-950/70 p-2 text-xs">
                      <div className="flex items-center justify-between"><span>{r.companyName}</span><span className="text-rose-300">Hata: {r.openErrors}</span></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-4">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold">Tenant Intelligence</h3>
                  <p className="mt-1 text-xs text-muted-foreground">Global arama ve operasyon aksiyonlari</p>
                </div>
                <Input value={globalSearch} onChange={(e) => setGlobalSearch(e.target.value)} placeholder="tenant / endpoint / kullanici" className="w-full max-w-md bg-slate-950 border-slate-700 text-slate-100" />
              </div>

              {globalSearch ? (
                <div className="mt-3 rounded-lg border border-slate-800 bg-slate-950/70 p-3">
                  {globalSearchRows.length === 0 ? (
                    <div className="text-sm text-muted-foreground">Sonuc yok.</div>
                  ) : (
                    <div className="space-y-2">
                      {globalSearchRows.map((row) => (
                        <button key={row.key} type="button" onClick={() => {
                          if (row.userId) {
                            setSelectedId(row.userId);
                            setQuickTenantId(row.userId);
                            setActiveTab("users");
                            return;
                          }
                          setActiveTab("errors");
                        }} className="w-full rounded-md border border-slate-800 bg-slate-900 p-2 text-left hover:border-slate-700">
                          <div className="text-xs text-muted-foreground uppercase">{row.type}</div>
                          <div className="text-sm font-medium text-slate-100 truncate">{row.title}</div>
                          <div className="text-xs text-muted-foreground truncate">{row.subtitle}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}

              <div className="mt-4 grid gap-4 lg:grid-cols-[1.2fr_1fr]">
                <div className="rounded-lg border border-slate-800 bg-slate-950/70 p-3">
                  <div className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Quick Actions</div>
                  <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto_auto]">
                    <Select
                      value={quickTenantId || "__none__"}
                      onValueChange={(v) => setQuickTenantId(v === "__none__" ? "" : v)}
                    >
                      <SelectTrigger className="h-10 border-slate-700 bg-slate-950 px-3 text-sm text-slate-100">
                        <SelectValue placeholder="Tenant secin" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Tenant secin</SelectItem>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={String(u.id)}>{u.company_name || u.email}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button disabled={quickActionLoading || !quickTenantId} onClick={() => runQuickPlanChange("starter")} className="bg-sky-600 hover:bg-sky-500 text-white">Paket: Starter</Button>
                    <Button disabled={quickActionLoading || !quickTenantId} onClick={() => runQuickPlanChange("growth")} className="bg-indigo-600 hover:bg-indigo-500 text-white">Paket: Pro</Button>
                    <Button disabled={quickActionLoading || !quickTenantId} onClick={runQuickSuspend} className="bg-rose-600 hover:bg-rose-500 text-white">Suspend</Button>
                  </div>
                  {quickTargetUser ? <div className="mt-2 text-xs text-muted-foreground">Hedef: {quickTargetUser.company_name || quickTargetUser.email}</div> : null}
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-950/70 p-3">
                  <div className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Anlik Durum</div>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center justify-between"><span className="text-muted-foreground">Open Error</span><span className="text-rose-300">{overviewStats.error_24h}</span></div>
                    <div className="flex items-center justify-between"><span className="text-muted-foreground">Riskli Tenant</span><span className="text-amber-300">{tenantIntelRows.filter((x) => x.healthScore !== "A").length}</span></div>
                    <div className="flex items-center justify-between"><span className="text-muted-foreground">MRR</span><span className="text-emerald-300">₺{toNum(overviewStats.mrr_current).toLocaleString("tr-TR")}</span></div>
                  </div>
                </div>
              </div>

              <div className="mt-4 overflow-auto rounded-lg border border-slate-800">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-950/70 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left">Firma</th>
                      <th className="px-3 py-2 text-left">Paket</th>
                      <th className="px-3 py-2 text-left">Health</th>
                      <th className="px-3 py-2 text-left">API Limit</th>
                      <th className="px-3 py-2 text-left">Son Login</th>
                      <th className="px-3 py-2 text-left">Odeme</th>
                      <th className="px-3 py-2 text-left">Hata</th>
                      <th className="px-3 py-2 text-left">Ticket</th>
                      <th className="px-3 py-2 text-left">Aksiyon</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenantIntelRows.slice(0, 50).map((r) => (
                      <tr key={r.userId} className="group border-t border-slate-800/70">
                        <td className="px-3 py-2">
                          <div className="font-medium">{r.companyName}</div>
                          <div className="text-xs text-muted-foreground">{r.email}</div>
                        </td>
                        <td className="px-3 py-2 capitalize">{r.plan}</td>
                        <td className="px-3 py-2">
                          <span className={`rounded px-2 py-0.5 text-xs ${r.healthScore === "A" ? "bg-emerald-900/40 text-emerald-200" : r.healthScore === "B" ? "bg-amber-900/40 text-amber-200" : "bg-rose-900/40 text-rose-200"}`}>{r.healthScore}</span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="h-2 w-28 rounded-full bg-slate-800">
                            <div className={`h-2 rounded-full ${r.usagePct > 90 ? "bg-rose-500" : r.usagePct > 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, Math.max(2, r.usagePct))}%` }} />
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">%{toNum(r.usagePct).toFixed(0)}</div>
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{toDatetimeLocal(r.lastLoginAt || "").replace("T", " ") || "-"}</td>
                        <td className="px-3 py-2"><span className={`rounded px-2 py-0.5 text-xs ${r.paymentStatus === "active" ? "bg-emerald-900/40 text-emerald-200" : "bg-amber-900/40 text-amber-200"}`}>{r.paymentStatus}</span></td>
                        <td className="px-3 py-2">{r.openErrors}</td>
                        <td className="px-3 py-2">{r.supportTickets}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <Button size="sm" className="h-7 bg-sky-600 hover:bg-sky-500 text-white" onClick={() => toast.info("Impersonate yakinda aktif edilecek.")}>Impersonate</Button>
                            <div className="hidden items-center gap-1 group-hover:flex">
                              <Button size="sm" variant="outline" className="h-7 border-slate-700 bg-slate-900 text-slate-200" onClick={() => { setQuickTenantId(r.userId); runQuickPlanChange("growth", r.userId); }}>Paket</Button>
                              <Button size="sm" variant="outline" className="h-7 border-rose-700 bg-rose-950/20 text-rose-300" onClick={() => { setQuickTenantId(r.userId); runQuickSuspend(r.userId); }}>Suspend</Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <details className="mt-4 rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                <summary className="cursor-pointer text-sm font-medium">Derin Analitik (Opsiyonel)</summary>
                <div className="mt-3 grid gap-3 lg:grid-cols-2">
                  <div className="h-60 rounded-lg border border-slate-800 bg-slate-950/70 p-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={moduleUsageRows}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                        <XAxis dataKey="module" stroke="#94a3b8" hide />
                        <YAxis stroke="#94a3b8" />
                        <ReTooltip />
                        <Bar dataKey="value" fill="#38bdf8" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="h-60 rounded-lg border border-slate-800 bg-slate-950/70 p-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={endpointUsageRows}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                        <XAxis dataKey="name" stroke="#94a3b8" hide />
                        <YAxis stroke="#94a3b8" />
                        <ReTooltip />
                        <Bar dataKey="value" fill="#f97316" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="lg:col-span-2 h-52 rounded-lg border border-slate-800 bg-slate-950/70 p-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={hourlyApiRows}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                        <XAxis dataKey="hour" stroke="#94a3b8" />
                        <YAxis stroke="#94a3b8" />
                        <ReTooltip />
                        <Line type="monotone" dataKey="traffic" stroke="#22d3ee" dot={false} strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </details>
            </div>
          </div>
        )}

        {activeTab === "settings" && settingsSection === "integrations" && (
          <div className="mt-6 rounded-xl border border-slate-800 bg-slate-900 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold">Uygulama / Entegrasyon Modulleri</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Global kapattiginiz moduller tum tenantlarda gizlenir. Tenant override ile sadece secili firma icin acip kapatabilirsiniz.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: "all", label: "Tumu" },
                  { key: "application", label: "Uygulama" },
                  { key: "integration", label: "Entegrasyon" },
                  { key: "local_ecommerce", label: "Local E-Ticaret" },
                ].map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setProviderClassFilter(f.key)}
                    className={`rounded-md border px-2.5 py-1 text-xs ${
                      providerClassFilter === f.key
                        ? "border-sky-500 bg-sky-500/20 text-sky-200"
                        : "border-slate-700 bg-slate-950 text-slate-300"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-[1fr_1fr]">
              <div>
                <Label className="text-slate-300">Tenant Secimi (Override)</Label>
                <select
                  value={providerTenantId}
                  onChange={(e) => setProviderTenantId(e.target.value)}
                  className="mt-1 h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 text-sm text-slate-100"
                >
                  <option value="">Tenant secin</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {(u.company_name || u.full_name || u.email || u.id)}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Secili: {selectedProviderTenant?.company_name || selectedProviderTenant?.email || "-"}
                </p>
              </div>
            </div>

            <div className="mt-4 overflow-hidden rounded-lg border border-slate-800">
              <div className="grid grid-cols-12 border-b border-slate-800 bg-slate-950 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <div className="col-span-3">Modul</div>
                <div className="col-span-2">Sinif</div>
                <div className="col-span-2">Tur</div>
                <div className="col-span-2">Global</div>
                <div className="col-span-3">Tenant Override</div>
              </div>
              <div className="max-h-[460px] divide-y divide-slate-800 overflow-y-auto">
                {providerLoading ? (
                  <div className="px-3 py-8 text-sm text-muted-foreground">Yukleniyor...</div>
                ) : visibleProviderRows.length === 0 ? (
                  <div className="px-3 py-8 text-sm text-muted-foreground">Kayit bulunamadi.</div>
                ) : (
                  visibleProviderRows.map((row) => (
                    <div key={row.key} className="grid grid-cols-12 items-center px-3 py-2.5 text-sm">
                      <div className="col-span-3">
                        <div className="font-medium text-slate-100">{row.label}</div>
                        <div className="text-[11px] text-muted-foreground">{row.key}</div>
                      </div>
                      <div className="col-span-2 text-slate-300">{row.module_class_label || "-"}</div>
                      <div className="col-span-2 text-slate-300">{row.kind_label || row.kind || "-"}</div>
                      <div className="col-span-2">
                        <button
                          type="button"
                          disabled={providerSavingKey === `global:${row.key}`}
                          onClick={() => updateGlobalProvider(row.key, !Boolean(row.global_enabled))}
                          className={`rounded-md px-2 py-1 text-xs ${
                            row.global_enabled
                              ? "bg-emerald-900/40 text-emerald-200"
                              : "bg-rose-900/40 text-rose-200"
                          }`}
                        >
                          {providerSavingKey === `global:${row.key}` ? "..." : row.global_enabled ? "Acik" : "Kapali"}
                        </button>
                      </div>
                      <div className="col-span-3">
                        <select
                          value={row.user_enabled === null || row.user_enabled === undefined ? "inherit" : row.user_enabled ? "enabled" : "disabled"}
                          disabled={!providerTenantId || providerSavingKey === `tenant:${row.key}`}
                          onChange={(e) => {
                            const value = e.target.value;
                            const mapped = value === "inherit" ? null : value === "enabled";
                            updateTenantProvider(row.key, mapped);
                          }}
                          className="h-8 w-full rounded-md border border-slate-700 bg-slate-950 px-2 text-xs text-slate-200 disabled:opacity-50"
                        >
                          <option value="inherit">Miras (Global)</option>
                          <option value="enabled">Acik</option>
                          <option value="disabled">Kapali</option>
                        </select>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "settings" && settingsSection === "packages" && (
          <div className="mt-6 rounded-xl border border-slate-800 bg-slate-900 p-4">
            <h3 className="text-base font-semibold">Paket İçerikleri ve Fiyatlandırma</h3>
            <p className="mt-1 text-xs text-muted-foreground">Paneldeki Faturalandırma sayfasında görünen paketleri buradan yönetebilirsiniz.</p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div>
                <Label className="text-slate-300">Başlık</Label>
                <Input
                  value={billingPackages.title}
                  onChange={(e) => setBillingPackages((prev) => ({ ...prev, title: e.target.value }))}
                  className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
              <div>
                <Label className="text-slate-300">Ücretsiz Deneme (gün)</Label>
                <Input
                  type="number"
                  min={0}
                  value={billingPackages.trial_days}
                  onChange={(e) => setBillingPackages((prev) => ({ ...prev, trial_days: e.target.value }))}
                  className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-slate-300">Alt Başlık</Label>
                <Input
                  value={billingPackages.subtitle}
                  onChange={(e) => setBillingPackages((prev) => ({ ...prev, subtitle: e.target.value }))}
                  className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
            </div>
            <div className="mt-4 grid gap-3 lg:grid-cols-3">
              {(billingPackages.packages || []).map((pkg, idx) => (
                <div key={pkg.key || idx} className="rounded-lg border border-slate-800 bg-slate-950 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">Paket #{idx + 1}</div>
                    <Button type="button" variant="outline" className="h-7 border-rose-700 bg-rose-950/20 text-rose-300" onClick={() => removePackageCard(idx)}>
                      Sil
                    </Button>
                  </div>
                  <div className="grid gap-2">
                    <Input value={pkg.key || ""} onChange={(e) => updatePackageField(idx, "key", e.target.value)} placeholder="key" className="bg-slate-900 border-slate-700 text-slate-100" />
                    <Input value={pkg.name || ""} onChange={(e) => updatePackageField(idx, "name", e.target.value)} placeholder="Paket Adı" className="bg-slate-900 border-slate-700 text-slate-100" />
                    <Input value={pkg.badge || ""} onChange={(e) => updatePackageField(idx, "badge", e.target.value)} placeholder="Badge" className="bg-slate-900 border-slate-700 text-slate-100" />
                    <Input value={pkg.description || ""} onChange={(e) => updatePackageField(idx, "description", e.target.value)} placeholder="Açıklama" className="bg-slate-900 border-slate-700 text-slate-100" />
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="number" value={pkg.monthly_price ?? ""} onChange={(e) => updatePackageField(idx, "monthly_price", e.target.value)} placeholder="Aylık" className="bg-slate-900 border-slate-700 text-slate-100" />
                      <Input type="number" value={pkg.yearly_price ?? ""} onChange={(e) => updatePackageField(idx, "yearly_price", e.target.value)} placeholder="Yıllık" className="bg-slate-900 border-slate-700 text-slate-100" />
                    </div>
                    <Input value={pkg.yearly_note || ""} onChange={(e) => updatePackageField(idx, "yearly_note", e.target.value)} placeholder="Yıllık not" className="bg-slate-900 border-slate-700 text-slate-100" />
                    <Input value={pkg.cta || ""} onChange={(e) => updatePackageField(idx, "cta", e.target.value)} placeholder="Buton Metni" className="bg-slate-900 border-slate-700 text-slate-100" />
                    <label className="inline-flex items-center gap-2 text-xs text-slate-300">
                      <input type="checkbox" checked={Boolean(pkg.highlight)} onChange={(e) => updatePackageField(idx, "highlight", e.target.checked)} />
                      Öne Çıkar
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={addPackageCard} variant="outline" className="border-slate-700 bg-slate-950 text-slate-100">
                Yeni Paket Ekle
              </Button>
              <Button onClick={saveBillingPackages} disabled={packageSaving} className="bg-sky-600 hover:bg-sky-500 text-white">
                {packageSaving ? "Kaydediliyor..." : "Paketleri Kaydet"}
              </Button>
            </div>
          </div>
        )}

        {activeTab === "settings" && settingsSection === "payments" && (
          <div className="mt-6 rounded-xl border border-slate-800 bg-slate-900 p-4">
            <h3 className="text-base font-semibold">PayTR Ödeme Ayarları</h3>
            <p className="mt-1 text-xs text-muted-foreground">Kart verisini asla saklamayın. Sadece PayTR token/masked meta ile çalışın.</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="inline-flex items-center gap-2 text-sm text-slate-300 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={Boolean(paytrSettings.enabled)}
                  onChange={(e) => setPaytrSettings((prev) => ({ ...prev, enabled: e.target.checked }))}
                />
                PayTR entegrasyonunu aktif et
              </label>
              <div>
                <Label className="text-slate-300">Merchant ID</Label>
                <Input
                  value={paytrSettings.merchant_id || ""}
                  onChange={(e) => setPaytrSettings((prev) => ({ ...prev, merchant_id: e.target.value }))}
                  placeholder={paytrSettings.merchant_id_masked || "XXXXXX"}
                  className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
              <div>
                <Label className="text-slate-300">Test Mode</Label>
                <select
                  value={Number(paytrSettings.test_mode || 1)}
                  onChange={(e) => setPaytrSettings((prev) => ({ ...prev, test_mode: Number(e.target.value || 1) }))}
                  className="mt-1 h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 text-sm text-slate-100"
                >
                  <option value={1}>Açık (Test)</option>
                  <option value={0}>Kapalı (Canlı)</option>
                </select>
              </div>
              <div>
                <Label className="text-slate-300">Merchant Key</Label>
                <Input
                  type="password"
                  value={paytrSettings.merchant_key || ""}
                  onChange={(e) => setPaytrSettings((prev) => ({ ...prev, merchant_key: e.target.value }))}
                  placeholder={paytrSettings.merchant_key_masked || "••••••••"}
                  className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
              <div>
                <Label className="text-slate-300">Merchant Salt</Label>
                <Input
                  type="password"
                  value={paytrSettings.merchant_salt || ""}
                  onChange={(e) => setPaytrSettings((prev) => ({ ...prev, merchant_salt: e.target.value }))}
                  placeholder={paytrSettings.merchant_salt_masked || "••••••••"}
                  className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
              <div>
                <Label className="text-slate-300">OK URL</Label>
                <Input
                  value={paytrSettings.merchant_ok_url || ""}
                  onChange={(e) => setPaytrSettings((prev) => ({ ...prev, merchant_ok_url: e.target.value }))}
                  placeholder="https://app.domain/panel/hesabim/faturalandirma?payment=ok"
                  className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
              <div>
                <Label className="text-slate-300">Fail URL</Label>
                <Input
                  value={paytrSettings.merchant_fail_url || ""}
                  onChange={(e) => setPaytrSettings((prev) => ({ ...prev, merchant_fail_url: e.target.value }))}
                  placeholder="https://app.domain/panel/hesabim/faturalandirma?payment=fail"
                  className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={Boolean(paytrSettings.save_card_default)}
                  onChange={(e) => setPaytrSettings((prev) => ({ ...prev, save_card_default: e.target.checked }))}
                />
                Varsayılan olarak kart saklama seçeneğini aç
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={Boolean(paytrSettings.strict_mode)}
                  onChange={(e) => setPaytrSettings((prev) => ({ ...prev, strict_mode: e.target.checked }))}
                />
                Sıkı güvenlik modu (önerilen)
              </label>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={savePaytrSettings} disabled={paytrSettingsSaving} className="bg-sky-600 hover:bg-sky-500 text-white">
                {paytrSettingsSaving ? "Kaydediliyor..." : "PayTR Ayarlarını Kaydet"}
              </Button>
            </div>
          </div>
        )}

        {activeTab === "settings" && settingsSection === "media" && (
          <div className="mt-6 rounded-xl border border-slate-800 bg-slate-900 p-4">
            <h3 className="text-base font-semibold">R2 Medya Ayarları</h3>
            <p className="mt-1 text-xs text-muted-foreground">Woo urun gorselleri ve benzer medya dosyalari icin merkezi bucket kurallari.</p>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="inline-flex items-center gap-2 text-sm text-slate-300 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={Boolean(mediaR2Settings.enabled)}
                  onChange={(e) => setMediaR2Settings((prev) => ({ ...prev, enabled: e.target.checked }))}
                />
                R2 medya yonetimini aktif et
              </label>
              <div className="sm:col-span-2">
                <Label className="text-slate-300">R2 Endpoint URL</Label>
                <Input
                  value={mediaR2Settings.r2_endpoint_url || ""}
                  onChange={(e) => setMediaR2Settings((prev) => ({ ...prev, r2_endpoint_url: e.target.value }))}
                  placeholder="https://<accountid>.r2.cloudflarestorage.com"
                  className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-slate-300">R2 Public Base URL</Label>
                <Input
                  value={mediaR2Settings.r2_public_base_url || ""}
                  onChange={(e) => setMediaR2Settings((prev) => ({ ...prev, r2_public_base_url: e.target.value }))}
                  placeholder="https://cdn.obihub.app"
                  className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
              <div>
                <Label className="text-slate-300">R2 Access Key ID</Label>
                <Input
                  value={mediaR2Settings.r2_access_key_id || ""}
                  onChange={(e) => setMediaR2Settings((prev) => ({ ...prev, r2_access_key_id: e.target.value }))}
                  placeholder={mediaR2Settings.r2_access_key_id_masked || "Yeni key girin"}
                  className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
              <div>
                <Label className="text-slate-300">R2 Secret Access Key</Label>
                <Input
                  type="password"
                  value={mediaR2Settings.r2_secret_access_key || ""}
                  onChange={(e) => setMediaR2Settings((prev) => ({ ...prev, r2_secret_access_key: e.target.value }))}
                  placeholder={mediaR2Settings.r2_secret_access_key_masked || "Yeni secret girin"}
                  className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={Boolean(mediaR2Settings.auto_bucket_by_domain)}
                  onChange={(e) => setMediaR2Settings((prev) => ({ ...prev, auto_bucket_by_domain: e.target.checked }))}
                />
                Domain bazli bucket adi uret
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={Boolean(mediaR2Settings.auto_create_bucket)}
                  onChange={(e) => setMediaR2Settings((prev) => ({ ...prev, auto_create_bucket: e.target.checked }))}
                />
                Bucket yoksa otomatik olustur
              </label>
              <div>
                <Label className="text-slate-300">Bucket Prefix</Label>
                <Input
                  value={mediaR2Settings.bucket_prefix || ""}
                  onChange={(e) => setMediaR2Settings((prev) => ({ ...prev, bucket_prefix: e.target.value }))}
                  className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
              <div>
                <Label className="text-slate-300">Key Prefix</Label>
                <Input
                  value={mediaR2Settings.key_prefix || ""}
                  onChange={(e) => setMediaR2Settings((prev) => ({ ...prev, key_prefix: e.target.value }))}
                  className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
              <div>
                <Label className="text-slate-300">Varsayilan Bucket</Label>
                <Input
                  value={mediaR2Settings.default_bucket || ""}
                  onChange={(e) => setMediaR2Settings((prev) => ({ ...prev, default_bucket: e.target.value }))}
                  className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
              <div>
                <Label className="text-slate-300">Varsayilan Public Base URL</Label>
                <Input
                  value={mediaR2Settings.default_public_base_url || ""}
                  onChange={(e) => setMediaR2Settings((prev) => ({ ...prev, default_public_base_url: e.target.value }))}
                  placeholder="https://cdn.obihub.app"
                  className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-slate-300">Public URL Template (opsiyonel)</Label>
                <Input
                  value={mediaR2Settings.public_base_url_template || ""}
                  onChange={(e) => setMediaR2Settings((prev) => ({ ...prev, public_base_url_template: e.target.value }))}
                  placeholder="https://{bucket}.r2.dev"
                  className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
            </div>

            <div className="mt-5 rounded-lg border border-slate-800 p-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-200">Domain -> Bucket Kurallari</h4>
                <Button type="button" onClick={addMediaR2DomainRule} variant="outline" className="h-8 border-slate-700 bg-slate-950 text-slate-100">
                  Kural Ekle
                </Button>
              </div>
              <div className="mt-3 space-y-2">
                {(mediaR2Settings.domain_buckets || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground">Henüz kural yok. Domain bazlı ayrım için satır ekleyin.</div>
                ) : (
                  (mediaR2Settings.domain_buckets || []).map((row, idx) => (
                    <div key={`${row.domain || "new"}-${idx}`} className="grid gap-2 rounded border border-slate-800 bg-slate-950 p-2 lg:grid-cols-[1fr_1fr_1fr_auto]">
                      <Input
                        value={row.domain || ""}
                        onChange={(e) => updateMediaR2DomainRule(idx, "domain", e.target.value)}
                        placeholder="bike35.com"
                        className="bg-slate-900 border-slate-700 text-slate-100"
                      />
                      <Input
                        value={row.bucket || ""}
                        onChange={(e) => updateMediaR2DomainRule(idx, "bucket", e.target.value)}
                        placeholder="obihub-bike35-media"
                        className="bg-slate-900 border-slate-700 text-slate-100"
                      />
                      <Input
                        value={row.public_base_url || ""}
                        onChange={(e) => updateMediaR2DomainRule(idx, "public_base_url", e.target.value)}
                        placeholder="https://cdn.bike35.com"
                        className="bg-slate-900 border-slate-700 text-slate-100"
                      />
                      <Button type="button" onClick={() => removeMediaR2DomainRule(idx)} variant="outline" className="h-10 border-rose-700 bg-rose-900/20 text-rose-300">
                        Sil
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="mt-4">
              <Button onClick={saveMediaR2Settings} disabled={mediaR2Saving} className="bg-sky-600 hover:bg-sky-500 text-white">
                {mediaR2Saving ? "Kaydediliyor..." : "R2 Ayarlarini Kaydet"}
              </Button>
            </div>
          </div>
        )}

        {activeTab === "users" && (
          <div className="mt-6 space-y-4">
            <div className="grid gap-3 md:grid-cols-5">
              <div className="rounded-xl border border-sky-900/40 bg-gradient-to-br from-sky-950/40 to-slate-900 p-3">
                <div className="text-xs text-muted-foreground">Toplam Tenant</div>
                <div className="mt-1 text-xl font-semibold text-sky-300">{usersSummary.total}</div>
              </div>
              <div className="rounded-xl border border-emerald-900/40 bg-gradient-to-br from-emerald-950/30 to-slate-900 p-3">
                <div className="text-xs text-muted-foreground">Aktif</div>
                <div className="mt-1 text-xl font-semibold text-emerald-300">{usersSummary.active}</div>
              </div>
              <div className="rounded-xl border border-amber-900/40 bg-gradient-to-br from-amber-950/30 to-slate-900 p-3">
                <div className="text-xs text-muted-foreground">Askıda</div>
                <div className="mt-1 text-xl font-semibold text-amber-300">{usersSummary.suspended}</div>
              </div>
              <div className="rounded-xl border border-rose-900/40 bg-gradient-to-br from-rose-950/30 to-slate-900 p-3">
                <div className="text-xs text-muted-foreground">Süresi Biten</div>
                <div className="mt-1 text-xl font-semibold text-rose-300">{usersSummary.expired}</div>
              </div>
              <div className="rounded-xl border border-violet-900/40 bg-gradient-to-br from-violet-950/30 to-slate-900 p-3">
                <div className="text-xs text-muted-foreground">Enterprise</div>
                <div className="mt-1 text-xl font-semibold text-violet-300">{usersSummary.enterprise}</div>
              </div>
            </div>
            <div className="grid gap-4 lg:grid-cols-[1.1fr_1fr]">
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <div className="flex items-center justify-between gap-3">
                <Label className="text-slate-300">Kayıtlı Kullanıcılar</Label>
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="E-posta / şirket / isim ara"
                  className="max-w-xs bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
              <div className="mt-3">
                <Button onClick={loadData} variant="outline" className="h-8 border-slate-700 bg-slate-950 text-slate-200">
                  Ara
                </Button>
              </div>

              <div className="mt-4 space-y-2">
                {loading ? (
                  <div className="text-sm text-muted-foreground">Yükleniyor...</div>
                ) : users.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Kullanıcı bulunamadı.</div>
                ) : (
                  users.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => setSelectedId(u.id)}
                      className={`w-full text-left rounded-lg border p-3 transition ${
                        selectedId === u.id
                          ? "border-sky-500 bg-sky-500/10"
                          : "border-slate-800 bg-slate-950 hover:border-slate-700"
                      }`}
                    >
                      <div className="font-medium truncate">{u.company_name || u.email}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{u.email}</div>
                      <div className="mt-1 flex flex-wrap gap-2 text-[11px]">
                        <span className="rounded bg-slate-800 px-2 py-0.5">UID: {u.uid || u.id}</span>
                        {u.company_uid ? <span className="rounded bg-slate-800 px-2 py-0.5">Firma UID: {u.company_uid}</span> : null}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                        <span className="rounded bg-slate-800 px-2 py-0.5">{u.role || "viewer"}</span>
                        <span className="rounded bg-slate-800 px-2 py-0.5">{u.subscription_status || "active"}</span>
                        <span className="rounded bg-slate-800 px-2 py-0.5">{u.subscription_plan || "trial"}</span>
                        <span className="rounded bg-slate-800 px-2 py-0.5">
                          {u.subscription_billing_cycle === "yearly" ? "yıllık" : "aylık"}
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              {!selectedUser ? (
                <div className="text-sm text-muted-foreground">Düzenlemek için kullanıcı seçin.</div>
              ) : (
                <>
                  <h2 className="text-lg font-semibold">Erişim Düzenle</h2>
                  <p className="mt-1 text-xs text-muted-foreground">{selectedUser.company_name || selectedUser.email}</p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    UID: {selectedUser.uid || selectedUser.id}
                    {selectedUser.company_uid ? ` • Firma UID: ${selectedUser.company_uid}` : ""}
                  </p>

                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label className="text-slate-300">Rol</Label>
                      <Select
                        value={normalizeRoleKey(form.role)}
                        onValueChange={(v) => setForm((prev) => ({ ...prev, role: normalizeRoleKey(v) }))}
                      >
                        <SelectTrigger className="mt-1 h-10 w-full border-slate-700 bg-slate-950 px-3 text-sm text-slate-100">
                          <SelectValue placeholder="Rol seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLE_OPTIONS.map((r) => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-300">Abonelik Planı</Label>
                      <Select
                        value={normalizePlanKey(form.subscription_plan)}
                        onValueChange={handlePlanChange}
                      >
                        <SelectTrigger className="mt-1 h-10 w-full border-slate-700 bg-slate-950 px-3 text-sm text-slate-100">
                          <SelectValue placeholder="Plan seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {SUBSCRIPTION_PLAN_OPTIONS.map((p) => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-300">Abonelik Durumu</Label>
                      <Select
                        value={normalizeStatusKey(form.subscription_status)}
                        onValueChange={handleStatusChange}
                      >
                        <SelectTrigger className="mt-1 h-10 w-full border-slate-700 bg-slate-950 px-3 text-sm text-slate-100">
                          <SelectValue placeholder="Durum seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-300">Abonelik Periyodu</Label>
                      <Select
                        value={normalizeCycleKey(form.subscription_billing_cycle)}
                        onValueChange={(v) => setForm((prev) => ({ ...prev, subscription_billing_cycle: normalizeCycleKey(v) }))}
                      >
                        <SelectTrigger className="mt-1 h-10 w-full border-slate-700 bg-slate-950 px-3 text-sm text-slate-100">
                          <SelectValue placeholder="Periyot seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Aylık</SelectItem>
                          <SelectItem value="yearly">Yıllık</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-300">Bitiş Tarihi</Label>
                      <Input
                        type="datetime-local"
                        value={form.subscription_expires_at}
                        onChange={(e) => setForm((prev) => ({ ...prev, subscription_expires_at: e.target.value }))}
                        className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                      />
                    </div>
                  </div>

                  <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                    <Label className="text-slate-300">Deneme Süresi Ekle</Label>
                    <div className="mt-2 grid gap-2 sm:grid-cols-[120px_1fr_auto]">
                      <Input
                        type="number"
                        min={1}
                        value={trialExtendDays}
                        onChange={(e) => setTrialExtendDays(e.target.value)}
                        className="bg-slate-950 border-slate-700 text-slate-100"
                      />
                      <Input
                        value={trialExtendReason}
                        onChange={(e) => setTrialExtendReason(e.target.value)}
                        placeholder="Neden (opsiyonel)"
                        className="bg-slate-950 border-slate-700 text-slate-100"
                      />
                      <Button onClick={extendTrial} disabled={trialExtending} className="bg-sky-600 hover:bg-sky-500 text-white">
                        {trialExtending ? "Uygulanıyor..." : "Süre Ekle"}
                      </Button>
                    </div>
                    <div className="mt-3 max-h-36 space-y-1 overflow-auto text-xs">
                      {trialHistory.length === 0 ? (
                        <div className="text-muted-foreground">Henüz deneme uzatma kaydı yok.</div>
                      ) : trialHistory.map((h) => (
                        <div key={h.id} className="rounded border border-slate-800 bg-slate-900 px-2 py-1.5 text-slate-300">
                          +{h.added_days} gün · {h.reason || "Sebep belirtilmedi"} · {h.created_by || "-"} · {toDatetimeLocal(h.created_at).replace("T", " ")}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                    <Label className="text-slate-300">Abonelik Silme Feedback Geçmişi</Label>
                    <div className="mt-3 max-h-44 space-y-1 overflow-auto text-xs">
                      {checkoutDeleteFeedback.length === 0 ? (
                        <div className="text-muted-foreground">Henüz silme feedback kaydı yok.</div>
                      ) : checkoutDeleteFeedback.map((h) => (
                        <div key={h.id} className="rounded border border-slate-800 bg-slate-900 px-2 py-1.5 text-slate-300">
                          {h.reason || "Sebep yok"} · {h.note || "Not yok"} · intent: {h.intent_id || "-"} · {toDatetimeLocal(h.created_at).replace("T", " ")}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-5">
                    <Label className="text-slate-300">Modül Yetkileri</Label>
                    <div className="mt-2 space-y-3">
                      {modules.map((m) => (
                        <div key={m.key} className="rounded-lg border border-slate-800 p-3">
                          <div className="text-sm font-medium">{m.label}</div>
                          <div className="mt-2 flex flex-wrap gap-3">
                            {Array.isArray(m.permissions) &&
                              m.permissions.map((perm) => (
                                <label key={perm} className="inline-flex items-center gap-2 text-xs text-slate-300">
                                  <input
                                    type="checkbox"
                                    checked={form.permissions.includes(perm)}
                                    onChange={() => togglePermission(perm)}
                                  />
                                  <span>{PERMISSION_LABELS[perm] || perm}</span>
                                  <span className="text-muted-foreground">({perm})</span>
                                </label>
                              ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-5">
                    <Label className="text-slate-300">Kullanım Limitleri Operasyonu</Label>
                    <div className="mt-2 rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                      <div className="text-xs text-muted-foreground">
                        Plan Baz Aktif Limitler: Starter için 1 Muhasebe + 1 Entegrasyon, Pro için 1 Muhasebe + 3 Entegrasyon, Enterprise için sınırsız.
                      </div>
                      <div className="mt-2 grid gap-2 sm:grid-cols-2 text-xs">
                        <div className="rounded border border-slate-800 bg-slate-900 px-2 py-1.5">E-Ticaret: <span className="text-slate-200">{formatLimit(baseLimits.max_store_integrations)}</span></div>
                        <div className="rounded border border-slate-800 bg-slate-900 px-2 py-1.5">Pazaryeri: <span className="text-slate-200">{formatLimit(baseLimits.max_marketplace_integrations)}</span></div>
                        <div className="rounded border border-slate-800 bg-slate-900 px-2 py-1.5">Kargo: <span className="text-slate-200">{formatLimit(baseLimits.max_cargo_integrations)}</span></div>
                        <div className="rounded border border-slate-800 bg-slate-900 px-2 py-1.5">Muhasebe: <span className="text-slate-200">{formatLimit(baseLimits.max_accounting_integrations)}</span></div>
                        <div className="rounded border border-slate-800 bg-slate-900 px-2 py-1.5">Toplam Entegrasyon: <span className="text-slate-200">{formatLimit(baseLimits.max_integrations)}</span></div>
                        <div className="rounded border border-slate-800 bg-slate-900 px-2 py-1.5">Maks. Ürün: <span className="text-slate-200">{formatLimit(baseLimits.max_products)}</span></div>
                      </div>
                    </div>

                    <div className="mt-3 rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                      <div className="text-xs text-slate-300">Geçici Limit Artırımı (delta)</div>
                      <div className="mt-2 grid gap-3 sm:grid-cols-2">
                        <div>
                          <Label className="text-muted-foreground text-xs">E-Ticaret +</Label>
                          <Input type="number" min={0} value={tempIncrease.max_store_integrations} onChange={(e) => setTempIncrease((p) => ({ ...p, max_store_integrations: Number(e.target.value || 0) }))} className="mt-1 bg-slate-950 border-slate-700 text-slate-100" />
                        </div>
                        <div>
                          <Label className="text-muted-foreground text-xs">Pazaryeri +</Label>
                          <Input type="number" min={0} value={tempIncrease.max_marketplace_integrations} onChange={(e) => setTempIncrease((p) => ({ ...p, max_marketplace_integrations: Number(e.target.value || 0) }))} className="mt-1 bg-slate-950 border-slate-700 text-slate-100" />
                        </div>
                        <div>
                          <Label className="text-muted-foreground text-xs">Kargo +</Label>
                          <Input type="number" min={0} value={tempIncrease.max_cargo_integrations} onChange={(e) => setTempIncrease((p) => ({ ...p, max_cargo_integrations: Number(e.target.value || 0) }))} className="mt-1 bg-slate-950 border-slate-700 text-slate-100" />
                        </div>
                        <div>
                          <Label className="text-muted-foreground text-xs">Muhasebe +</Label>
                          <Input type="number" min={0} value={tempIncrease.max_accounting_integrations} onChange={(e) => setTempIncrease((p) => ({ ...p, max_accounting_integrations: Number(e.target.value || 0) }))} className="mt-1 bg-slate-950 border-slate-700 text-slate-100" />
                        </div>
                        <div>
                          <Label className="text-muted-foreground text-xs">Toplam Entegrasyon +</Label>
                          <Input type="number" min={0} value={tempIncrease.max_integrations} onChange={(e) => setTempIncrease((p) => ({ ...p, max_integrations: Number(e.target.value || 0) }))} className="mt-1 bg-slate-950 border-slate-700 text-slate-100" />
                        </div>
                        <div>
                          <Label className="text-muted-foreground text-xs">Maks. Ürün +</Label>
                          <Input type="number" min={0} value={tempIncrease.max_products} onChange={(e) => setTempIncrease((p) => ({ ...p, max_products: Number(e.target.value || 0) }))} className="mt-1 bg-slate-950 border-slate-700 text-slate-100" />
                        </div>
                        <div>
                          <Label className="text-muted-foreground text-xs">Aylık Sipariş +</Label>
                          <Input type="number" min={0} value={tempIncrease.max_orders_monthly} onChange={(e) => setTempIncrease((p) => ({ ...p, max_orders_monthly: Number(e.target.value || 0) }))} className="mt-1 bg-slate-950 border-slate-700 text-slate-100" />
                        </div>
                        <div>
                          <Label className="text-muted-foreground text-xs">Günlük API +</Label>
                          <Input type="number" min={0} value={tempIncrease.max_api_calls_daily} onChange={(e) => setTempIncrease((p) => ({ ...p, max_api_calls_daily: Number(e.target.value || 0) }))} className="mt-1 bg-slate-950 border-slate-700 text-slate-100" />
                        </div>
                      </div>
                      <div className="mt-3 flex gap-2">
                        <Button type="button" onClick={applyTemporaryIncrease} className="bg-amber-600 hover:bg-amber-500 text-white">Geçici Artırımı Uygula</Button>
                        <Button type="button" variant="outline" onClick={resetToBaseLimits} className="border-slate-700 text-slate-200">Plan Baz Limite Dön</Button>
                      </div>
                    </div>

                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <div>
                        <Label className="text-muted-foreground text-xs">E-Ticaret Entegrasyon Limiti</Label>
                        <Input
                          type="number"
                          min={0}
                          value={form.max_store_integrations}
                          onChange={(e) => setForm((prev) => ({ ...prev, max_store_integrations: e.target.value }))}
                          className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                        />
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs">Pazaryeri Entegrasyon Limiti</Label>
                        <Input
                          type="number"
                          min={0}
                          value={form.max_marketplace_integrations}
                          onChange={(e) => setForm((prev) => ({ ...prev, max_marketplace_integrations: e.target.value }))}
                          className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                        />
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs">Kargo Entegrasyon Limiti</Label>
                        <Input
                          type="number"
                          min={0}
                          value={form.max_cargo_integrations}
                          onChange={(e) => setForm((prev) => ({ ...prev, max_cargo_integrations: e.target.value }))}
                          className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                        />
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs">Muhasebe Entegrasyon Limiti</Label>
                        <Input
                          type="number"
                          min={0}
                          value={form.max_accounting_integrations}
                          onChange={(e) => setForm((prev) => ({ ...prev, max_accounting_integrations: e.target.value }))}
                          className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                        />
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs">Maks. Entegrasyon</Label>
                        <Input
                          type="number"
                          min={0}
                          value={form.max_integrations}
                          onChange={(e) => setForm((prev) => ({ ...prev, max_integrations: e.target.value }))}
                          className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                        />
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs">Maks. Ürün</Label>
                        <Input
                          type="number"
                          min={0}
                          value={form.max_products}
                          onChange={(e) => setForm((prev) => ({ ...prev, max_products: e.target.value }))}
                          className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                        />
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs">Aylık Maks. Sipariş</Label>
                        <Input
                          type="number"
                          min={0}
                          value={form.max_orders_monthly}
                          onChange={(e) => setForm((prev) => ({ ...prev, max_orders_monthly: e.target.value }))}
                          className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                        />
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs">Günlük Maks. API Çağrısı</Label>
                        <Input
                          type="number"
                          min={0}
                          value={form.max_api_calls_daily}
                          onChange={(e) => setForm((prev) => ({ ...prev, max_api_calls_daily: e.target.value }))}
                          className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                        />
                      </div>
                    </div>
                    <div className="mt-3">
                      <Label className="text-muted-foreground text-xs">Limit Değişiklik Notu (Audit)</Label>
                      <Input
                        value={form.limit_change_note}
                        onChange={(e) => setForm((prev) => ({ ...prev, limit_change_note: e.target.value }))}
                        placeholder="Örn: Geçici kampanya limiti artırımı"
                        className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                      />
                    </div>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2">
                    <Button onClick={saveAccess} disabled={saving} className="bg-sky-600 hover:bg-sky-500 text-white">
                      {saving ? "Kaydediliyor..." : "Kaydet"}
                    </Button>
                    <Button
                      onClick={deleteCompanyPermanently}
                      disabled={deletingCompany}
                      variant="destructive"
                      className="bg-rose-700 hover:bg-rose-600 text-white"
                    >
                      {deletingCompany ? "Siliniyor..." : "Firmayı Kalıcı Sil"}
                    </Button>
                  </div>
                  <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                    <Label className="text-slate-300">Limit Değişiklik Geçmişi</Label>
                    <div className="mt-3 max-h-44 space-y-1 overflow-auto text-xs">
                      {limitAuditLogs.length === 0 ? (
                        <div className="text-muted-foreground">Henüz limit değişiklik kaydı yok.</div>
                      ) : limitAuditLogs.map((h) => (
                        <div key={h.id} className="rounded border border-slate-800 bg-slate-900 px-2 py-1.5 text-slate-300">
                          {h.changed_by || "-"} · {toDatetimeLocal(h.changed_at).replace("T", " ")} · {h.note || "Not yok"}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            </div>
          </div>
        )}

        {activeTab === "usage" && (
          <div className="mt-6 space-y-4">
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-base font-semibold">Kullanım Kontrol Merkezi</h3>
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <label className="inline-flex items-center gap-2 text-slate-300">
                    <input type="checkbox" checked={usageAutoRefresh} onChange={(e) => setUsageAutoRefresh(e.target.checked)} />
                    Auto refresh
                  </label>
                  <select value={usageRefreshSeconds} onChange={(e) => setUsageRefreshSeconds(Number(e.target.value || 30))} className="h-8 rounded-md border border-slate-700 bg-slate-950 px-2 text-xs">
                    <option value={15}>15 sn</option>
                    <option value={30}>30 sn</option>
                    <option value={45}>45 sn</option>
                    <option value={60}>60 sn</option>
                  </select>
                  <span className="text-muted-foreground">Son güncelleme: {usageLastUpdatedAt ? new Date(usageLastUpdatedAt).toLocaleTimeString("tr-TR") : "-"}</span>
                </div>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-4">
                <div className="rounded-lg border border-sky-900/40 bg-slate-950/70 p-3">
                  <div className="text-xs text-muted-foreground">Toplam API Trafik</div>
                  <div className="mt-1 text-lg font-semibold text-sky-300">{toNum(usageSummary.totalEvents).toLocaleString("tr-TR")}</div>
                </div>
                <div className="rounded-lg border border-rose-900/40 bg-slate-950/70 p-3">
                  <div className="text-xs text-muted-foreground">Hatalı Çağrı</div>
                  <div className="mt-1 text-lg font-semibold text-rose-300">{toNum(usageSummary.totalErrors).toLocaleString("tr-TR")}</div>
                </div>
                <div className="rounded-lg border border-emerald-900/40 bg-slate-950/70 p-3">
                  <div className="text-xs text-muted-foreground">Aktif Tenant</div>
                  <div className="mt-1 text-lg font-semibold text-emerald-300">{usageSummary.activeTenants}</div>
                </div>
                <div className="rounded-lg border border-amber-900/40 bg-slate-950/70 p-3">
                  <div className="text-xs text-muted-foreground">Limit Riski (%90+)</div>
                  <div className="mt-1 text-lg font-semibold text-amber-300">{usageSummary.limitRiskTenants}</div>
                </div>
              </div>
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
                  <div className="mb-2 text-xs text-muted-foreground">Modül Bazlı API Dağılımı</div>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={usageModuleChart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                        <XAxis dataKey="module" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                        <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                        <ReTooltip />
                        <Bar dataKey="value" fill="#38bdf8" radius={[5, 5, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
                  <div className="mb-2 text-xs text-muted-foreground">En Yüksek Trafik Üreten Tenantlar</div>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={usageTopTenantsChart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                        <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                        <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                        <ReTooltip />
                        <Bar dataKey="value" fill="#34d399" radius={[5, 5, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid gap-4 lg:grid-cols-[1fr_1.2fr]">
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold">Kullanıcı Kullanım Matrisi</h3>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Aralık</Label>
                  <select
                    value={usageDays}
                    onChange={(e) => setUsageDays(Number(e.target.value))}
                    className="h-8 rounded-md border border-slate-700 bg-slate-950 px-2 text-xs"
                  >
                    <option value={7}>7</option>
                    <option value={30}>30</option>
                    <option value={90}>90</option>
                  </select>
                </div>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <Input
                  value={usageSearch}
                  onChange={(e) => setUsageSearch(e.target.value)}
                  placeholder="Kullanıcı ara (e-posta/şirket)"
                  className="h-8 bg-slate-950 border-slate-700 text-slate-100 text-xs"
                />
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={usageRoleFilter}
                    onChange={(e) => setUsageRoleFilter(e.target.value)}
                    className="h-8 rounded-md border border-slate-700 bg-slate-950 px-2 text-xs"
                  >
                    <option value="all">Tüm Roller</option>
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  <select
                    value={usageStatusFilter}
                    onChange={(e) => setUsageStatusFilter(e.target.value)}
                    className="h-8 rounded-md border border-slate-700 bg-slate-950 px-2 text-xs"
                  >
                    <option value="all">Tüm Durumlar</option>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={usagePlanFilter}
                    onChange={(e) => setUsagePlanFilter(e.target.value)}
                    className="h-8 rounded-md border border-slate-700 bg-slate-950 px-2 text-xs"
                  >
                    <option value="all">Tüm Planlar</option>
                    <option value="trial">trial</option>
                    <option value="starter">starter</option>
                    <option value="growth">growth</option>
                    <option value="enterprise">enterprise</option>
                  </select>
                  <select
                    value={usageModuleFilter}
                    onChange={(e) => setUsageModuleFilter(e.target.value)}
                    className="h-8 rounded-md border border-slate-700 bg-slate-950 px-2 text-xs"
                  >
                    <option value="all">Tüm Modüller</option>
                    {Object.entries(MODULE_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={usageActiveHours}
                    onChange={(e) => setUsageActiveHours(Number(e.target.value))}
                    className="h-8 rounded-md border border-slate-700 bg-slate-950 px-2 text-xs"
                  >
                    <option value={6}>Son 6 saat</option>
                    <option value={24}>Son 24 saat</option>
                    <option value={72}>Son 72 saat</option>
                    <option value={168}>Son 7 gün</option>
                  </select>
                  <Input
                    type="number"
                    min={0}
                    value={usageMinEvents}
                    onChange={(e) => setUsageMinEvents(Number(e.target.value || 0))}
                    placeholder="Min olay"
                    className="h-8 bg-slate-950 border-slate-700 text-slate-100 text-xs"
                  />
                </div>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Not: "Entegrasyonlar: 32 API" ifadesi, entegrasyon ekranlarına yapılan toplam API isteğini gösterir.
              </p>
              <div className="mt-3 space-y-2">
                {usageLoading ? (
                  <div className="text-sm text-muted-foreground">Yükleniyor...</div>
                ) : usageUsers.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Kullanım verisi yok.</div>
                ) : (
                  usageUsers.map((u) => (
                    <button
                      key={u.user_id}
                      type="button"
                      onClick={() => setSelectedUsageUserId(u.user_id)}
                      className={`w-full rounded-lg border p-3 text-left ${
                        selectedUsageUserId === u.user_id
                          ? "border-sky-500 bg-sky-500/10"
                          : "border-slate-800 bg-slate-950 hover:border-slate-700"
                      }`}
                    >
                      <div className="font-medium truncate">{u.company_name || u.email}</div>
                      <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                      <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                        <span className="rounded bg-slate-800 px-2 py-0.5">Aktif Modül (son {usageActiveHours} saat): {u.active_modules_recent || 0}</span>
                        <span className="rounded bg-slate-800 px-2 py-0.5">Toplam Modül: {u.active_modules || 0}</span>
                        <span className="rounded bg-slate-800 px-2 py-0.5">API Çağrısı: {u.total_events || 0}</span>
                        <span className="rounded bg-indigo-900/30 px-2 py-0.5">Ürün Kaydı: {u.entity_counts?.products_total || 0}</span>
                        <span className="rounded bg-indigo-900/30 px-2 py-0.5">Entegrasyon Kaydı: {u.entity_counts?.integrations_total || 0}</span>
                        <span className="rounded bg-indigo-900/30 px-2 py-0.5">Aktif Entegrasyon: {u.entity_counts?.integrations_active || 0}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {Object.entries(u.modules || {})
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 4)
                          .map(([k, v]) => (
                            <span key={k} className="rounded bg-slate-800/60 px-2 py-0.5 text-[10px] text-slate-300">
                              {MODULE_LABELS[k] || k}: {v} API
                            </span>
                          ))}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              {!usageDetail ? (
                <div className="text-sm text-muted-foreground">Detay için kullanıcı seçin.</div>
              ) : (
                <>
                  <h3 className="text-base font-semibold">Kullanım Detayı</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {usageDetail.user?.company_name || usageDetail.user?.email} • Son {usageDetail.days} gün
                  </p>

                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    <div className="rounded border border-slate-800 bg-slate-950 p-2 text-xs">
                      <div className="text-muted-foreground">Toplam API Olayı</div>
                      <div className="text-base font-semibold">{usageDetail.summary?.total_events || 0}</div>
                    </div>
                    <div className="rounded border border-slate-800 bg-slate-950 p-2 text-xs">
                      <div className="text-muted-foreground">Açık Hata Kaydı</div>
                      <div className="text-base font-semibold text-amber-300">{usageDetail.summary?.open_error_logs || 0}</div>
                    </div>
                    <div className="rounded border border-slate-800 bg-slate-950 p-2 text-xs">
                      <div className="text-muted-foreground">Başarılı Çağrı</div>
                      <div className="text-base font-semibold text-emerald-300">{usageDetail.summary?.success_count || 0}</div>
                    </div>
                    <div className="rounded border border-slate-800 bg-slate-950 p-2 text-xs">
                      <div className="text-muted-foreground">Hatalı Çağrı</div>
                      <div className="text-base font-semibold text-rose-300">{usageDetail.summary?.error_count || 0}</div>
                    </div>
                  </div>

                  <div className="mt-4 rounded-lg border border-slate-800 p-3">
                    <div className="text-sm font-medium mb-2">Modül API Etkileşimi (çağrı adedi)</div>
                    <div className="space-y-1 text-xs">
                      {Object.entries(usageDetail.module_counts || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between rounded bg-slate-950 px-2 py-1">
                          <span>{MODULE_LABELS[k] || k}</span>
                          <span className="text-slate-300">{v} çağrı</span>
                        </div>
                      ))}
                      {!Object.keys(usageDetail.module_counts || {}).length && <div className="text-muted-foreground">Veri yok</div>}
                    </div>
                  </div>

                  <div className="mt-4 rounded-lg border border-slate-800 p-3">
                    <div className="text-sm font-medium mb-2">Gerçek Kayıt Sayıları</div>
                    <div className="grid gap-2 sm:grid-cols-3 text-xs">
                      <div className="rounded bg-slate-950 px-2 py-2">
                        <div className="text-muted-foreground">Ürün Kaydı</div>
                        <div className="text-base font-semibold">{usageDetail.entity_counts?.products_total || 0}</div>
                      </div>
                      <div className="rounded bg-slate-950 px-2 py-2">
                        <div className="text-muted-foreground">Entegrasyon Kaydı</div>
                        <div className="text-base font-semibold">{usageDetail.entity_counts?.integrations_total || 0}</div>
                      </div>
                      <div className="rounded bg-slate-950 px-2 py-2">
                        <div className="text-muted-foreground">Aktif Entegrasyon</div>
                        <div className="text-base font-semibold">{usageDetail.entity_counts?.integrations_active || 0}</div>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                      {(usageDetail.entity_counts?.integrations_by_provider || []).map((x) => (
                        <span key={x.provider} className="rounded bg-slate-800 px-2 py-1">
                          {x.provider}: {x.count}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4 rounded-lg border border-slate-800 p-3">
                    <div className="text-sm font-medium mb-2">En Çok Kullanılan Endpointler</div>
                    <div className="space-y-1 text-xs">
                      {(usageDetail.top_endpoints || []).map((x) => (
                        <div key={x.endpoint} className="flex items-center justify-between rounded bg-slate-950 px-2 py-1">
                          <span className="truncate mr-2">{x.endpoint}</span>
                          <span>{x.count}</span>
                        </div>
                      ))}
                      {!(usageDetail.top_endpoints || []).length && <div className="text-muted-foreground">Veri yok</div>}
                    </div>
                  </div>
                </>
              )}
            </div>
            </div>
          </div>
        )}

        {activeTab === "errors" && (
          <div className="mt-6 space-y-4">
            <div className="rounded-xl border border-rose-900/40 bg-gradient-to-br from-rose-950/25 to-slate-900 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-base font-semibold text-rose-200">Error & Risk Operasyon Merkezi</h3>
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <label className="inline-flex items-center gap-2 text-slate-300">
                    <input type="checkbox" checked={errorsAutoRefresh} onChange={(e) => setErrorsAutoRefresh(e.target.checked)} />
                    Auto refresh
                  </label>
                  <select value={errorsRefreshSeconds} onChange={(e) => setErrorsRefreshSeconds(Number(e.target.value || 30))} className="h-8 rounded-md border border-slate-700 bg-slate-950 px-2 text-xs">
                    <option value={15}>15 sn</option>
                    <option value={30}>30 sn</option>
                    <option value={45}>45 sn</option>
                    <option value={60}>60 sn</option>
                  </select>
                  <span className="text-muted-foreground">Son güncelleme: {errorsLastUpdatedAt ? new Date(errorsLastUpdatedAt).toLocaleTimeString("tr-TR") : "-"}</span>
                </div>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-5">
                <div className="rounded-lg border border-rose-900/50 bg-slate-950/70 p-3">
                  <div className="text-xs text-muted-foreground">Kritik</div>
                  <div className="mt-1 text-lg font-semibold text-rose-300">{errorSummary.critical}</div>
                </div>
                <div className="rounded-lg border border-amber-900/40 bg-slate-950/70 p-3">
                  <div className="text-xs text-muted-foreground">Warning</div>
                  <div className="mt-1 text-lg font-semibold text-amber-300">{errorSummary.warning}</div>
                </div>
                <div className="rounded-lg border border-sky-900/40 bg-slate-950/70 p-3">
                  <div className="text-xs text-muted-foreground">Open</div>
                  <div className="mt-1 text-lg font-semibold text-sky-300">{errorSummary.open}</div>
                </div>
                <div className="rounded-lg border border-emerald-900/40 bg-slate-950/70 p-3">
                  <div className="text-xs text-muted-foreground">Resolved</div>
                  <div className="mt-1 text-lg font-semibold text-emerald-300">{errorSummary.resolved}</div>
                </div>
                <div className="rounded-lg border border-slate-700 bg-slate-950/70 p-3">
                  <div className="text-xs text-muted-foreground">Top Endpoint</div>
                  <div className="mt-1 truncate text-sm font-semibold text-slate-100">{errorSummary.topEndpoint}</div>
                </div>
              </div>
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
                  <div className="mb-2 text-xs text-muted-foreground">Severity Dağılımı</div>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={errorSeverityChart} dataKey="value" nameKey="name" innerRadius={45} outerRadius={72} paddingAngle={2}>
                          {errorSeverityChart.map((entry, idx) => (
                            <Cell key={`${entry.name}-${idx}`} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <ReTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
                  <div className="mb-2 text-xs text-muted-foreground">Tenant Bazlı Hata Yoğunluğu</div>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={errorTenantChart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                        <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                        <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                        <ReTooltip />
                        <Bar dataKey="value" fill="#f87171" radius={[5, 5, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-base font-semibold">Hata İzleme (Kullanıcı Bazlı)</h3>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  value={errorSearch}
                  onChange={(e) => setErrorSearch(e.target.value)}
                  placeholder="E-posta / şirket / mesaj ara"
                  className="h-9 w-56 bg-slate-950 border-slate-700 text-slate-100"
                />
                <select
                  value={errorStatusFilter}
                  onChange={(e) => setErrorStatusFilter(e.target.value)}
                  className="h-9 rounded-md border border-slate-700 bg-slate-950 px-2 text-sm"
                >
                  <option value="all">Tüm Durumlar</option>
                  <option value="open">Açık</option>
                  <option value="resolved">Çözüldü</option>
                  <option value="ignored">Ignore</option>
                </select>
                <select
                  value={errorSeverityFilter}
                  onChange={(e) => setErrorSeverityFilter(e.target.value)}
                  className="h-9 rounded-md border border-slate-700 bg-slate-950 px-2 text-sm"
                >
                  <option value="all">Tüm Seviyeler</option>
                  <option value="critical">critical</option>
                  <option value="error">error</option>
                  <option value="warning">warning</option>
                  <option value="info">info</option>
                </select>
                <Button onClick={loadErrorLogs} variant="outline" className="h-9 border-slate-700 bg-slate-950 text-slate-100">
                  Filtrele
                </Button>
                <Button
                  onClick={() => bulkDeleteErrorLogs({ forceAll: false })}
                  variant="outline"
                  className="h-9 border-rose-700 bg-rose-900/20 text-rose-200"
                >
                  Filtreye Göre Temizle
                </Button>
                <Button
                  onClick={() => bulkDeleteErrorLogs({ forceAll: true })}
                  variant="outline"
                  className="h-9 border-rose-700 bg-rose-900 text-rose-100"
                >
                  Tümünü Temizle
                </Button>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {errorLogs.length === 0 ? (
                <div className="text-sm text-muted-foreground">Kayıt bulunamadı.</div>
              ) : (
                errorLogs.map((r) => (
                  <div key={r.id} className="rounded-lg border border-slate-800 bg-slate-950 p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-medium">{r.company_name || r.email}</div>
                        <div className="text-xs text-muted-foreground">{r.email}</div>
                        <div className="mt-1 text-xs text-muted-foreground">{r.page || "-"}</div>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="rounded bg-slate-800 px-2 py-0.5">{r.severity}</span>
                        <span className="rounded bg-slate-800 px-2 py-0.5">{r.status}</span>
                        <span className="rounded bg-sky-900/30 px-2 py-0.5 text-sky-200">Tekrar: {r.repeat_count || 1}</span>
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-slate-200 break-words">{r.message}</div>
                  {r.stack ? <pre className="mt-2 rounded bg-black/30 p-2 text-[11px] text-slate-300 whitespace-pre-wrap break-words">{r.stack}</pre> : null}
                    <div className="mt-2 flex items-center gap-2">
                      <Button
                        onClick={() => markErrorStatus(r.id, "resolved")}
                        variant="outline"
                        className="h-8 border-emerald-700 bg-emerald-900/20 text-emerald-200"
                      >
                        Resolved
                      </Button>
                      <Button
                        onClick={() => markErrorStatus(r.id, "ignored")}
                        variant="outline"
                        className="h-8 border-slate-700 bg-slate-900 text-slate-200"
                      >
                        Ignore
                      </Button>
                      <span className="text-xs text-muted-foreground ml-auto">İlk: {r.created_at} • Son: {r.last_seen_at || r.updated_at || r.created_at}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
            </div>
          </div>
        )}

        {activeTab === "support" && (
          <div className="mt-6 grid gap-4 xl:grid-cols-[360px_1fr]">
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <h3 className="text-base font-semibold">Destek Talepleri</h3>
              <div className="mt-3 grid gap-2">
                <select
                  value={supportStatusFilter}
                  onChange={(e) => setSupportStatusFilter(e.target.value)}
                  className="h-9 rounded-md border border-slate-700 bg-slate-950 px-2 text-xs"
                >
                  <option value="all">Tüm Durumlar</option>
                  <option value="queued">Sırada</option>
                  <option value="open">Açık</option>
                  <option value="in_progress">Devam Ediyor</option>
                  <option value="waiting_customer">Müşteri Yanıtı Bekleniyor</option>
                  <option value="resolved">Çözüldü</option>
                  <option value="closed">Kapandı</option>
                </select>
                <Input
                  value={supportSearch}
                  onChange={(e) => setSupportSearch(e.target.value)}
                  placeholder="Firma, e-posta veya ticket no ara"
                  className="h-9 bg-slate-950 border-slate-700 text-slate-100 text-xs"
                />
              </div>
              <div className="mt-3 max-h-[62vh] overflow-y-auto space-y-2">
                {supportLoading ? (
                  <div className="text-sm text-muted-foreground">Yükleniyor...</div>
                ) : supportTickets.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Kayıt bulunamadı.</div>
                ) : (
                  supportTickets.map((ticket) => (
                    <button
                      key={ticket.id}
                      type="button"
                      onClick={() => setSelectedSupportTicketId(ticket.id)}
                      className={`w-full rounded-lg border p-3 text-left ${
                        selectedSupportTicketId === ticket.id
                          ? "border-sky-500 bg-sky-500/10"
                          : "border-slate-800 bg-slate-950 hover:border-slate-700"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="truncate text-sm font-semibold">{ticket.subject || "Destek Talebi"}</div>
                        {ticket.unread_from_customer ? <span className="mt-1 h-2.5 w-2.5 rounded-full bg-rose-500" /> : null}
                      </div>
                      <div className="mt-1 truncate text-xs text-muted-foreground">{ticket.company_name || ticket.email || "-"}</div>
                      <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{ticket.last_message_preview || "-"}</div>
                      <div className="mt-1 text-[11px] text-muted-foreground">{ticket.ticket_code || `#${String(ticket.id || "").slice(0, 8)}`}</div>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              {!selectedSupportTicketId ? (
                <div className="text-sm text-muted-foreground">Detay için soldan ticket seçin.</div>
              ) : (
                <>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-base font-semibold">{selectedSupportDetail?.subject || "Destek Talebi"}</h3>
                      <p className="mt-1 text-xs text-muted-foreground">Ticket: {selectedSupportDetail?.ticket_code || selectedSupportDetail?.id}</p>
                      <p className="text-xs text-muted-foreground">{selectedSupportDetail?.company_name || selectedSupportDetail?.email || "-"}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {["queued", "open", "in_progress", "waiting_customer", "resolved", "closed"].map((statusKey) => (
                        <Button
                          key={statusKey}
                          size="sm"
                          variant="outline"
                          className="h-7 border-slate-700 bg-slate-950 text-slate-200"
                          onClick={() => superChangeSupportStatus(statusKey)}
                        >
                          {statusKey === "queued"
                            ? "Sırada"
                            : statusKey === "open"
                            ? "Açık"
                            : statusKey === "in_progress"
                            ? "Devam"
                            : statusKey === "waiting_customer"
                            ? "Müşteri"
                            : statusKey === "resolved"
                            ? "Çözüldü"
                            : "Kapandı"}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4 max-h-[42vh] overflow-y-auto space-y-2 rounded-lg border border-slate-800 bg-slate-950/50 p-3">
                    {supportMessages.length === 0 ? (
                      <div className="text-sm text-muted-foreground">Mesaj bulunamadı.</div>
                    ) : (
                      supportMessages.map((msg) => {
                        const isCustomer = String(msg.sender_role || "") === "user";
                        return (
                          <div key={msg.id} className={`flex ${isCustomer ? "justify-start" : "justify-end"}`}>
                            <div className={`max-w-[86%] rounded-lg border px-3 py-2 text-sm ${isCustomer ? "border-slate-700 bg-slate-900 text-slate-100" : "border-sky-700 bg-sky-900/40 text-sky-100"}`}>
                              <div className="text-[11px] text-muted-foreground">{msg.sender_name || "Destek"}</div>
                              <div className="mt-1 whitespace-pre-wrap break-words">{msg.message}</div>
                              <div className="mt-1 text-[10px] text-muted-foreground">{toDatetimeLocal(msg.created_at || "").replace("T", " ") || "-"}</div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    {selectedSupportDetail?.typing?.user ? (
                      <div className="text-xs text-muted-foreground">Müşteri yazıyor...</div>
                    ) : null}
                  </div>

                  <div className="mt-3 flex items-end gap-2">
                    <textarea
                      value={supportReply}
                      onChange={(e) => {
                        setSupportReply(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          superReplySupportTicket();
                        }
                      }}
                      rows={3}
                      placeholder="Müşteriye yanıt yazın (Enter gönderir)..."
                      className="min-h-[84px] flex-1 resize-none rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
                    />
                    <Button onClick={superReplySupportTicket} disabled={supportSaving || String(supportReply || "").trim().length < 2} className="h-10 bg-sky-600 hover:bg-sky-500 text-white">
                      {supportSaving ? "Gönderiliyor..." : "Yanıtla"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === "settings" && settingsSection === "ai" && (
          <AiSettingsSection />
        )}

        {activeTab === "settings" && (settingsSection === "communications" || settingsSection === "admin") && (
          <>
          {settingsSection === "communications" && (
          <>
          <div className="mt-6 grid gap-4 lg:grid-cols-[1fr_1.1fr]">
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <h3 className="text-base font-semibold">AWS SES Ayarları</h3>
              <p className="mt-1 text-xs text-muted-foreground">Davet ve sistem mailleri için gönderim altyapısı.</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <label className="inline-flex items-center gap-2 text-sm text-slate-300 sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={Boolean(mailSettings.enabled)}
                    onChange={(e) => setMailSettings((prev) => ({ ...prev, enabled: e.target.checked }))}
                  />
                  Mail gönderimini aktif et
                </label>
                <div>
                  <Label className="text-slate-300">AWS Region</Label>
                  <Input
                    value={mailSettings.aws_region || ""}
                    onChange={(e) => setMailSettings((prev) => ({ ...prev, aws_region: e.target.value }))}
                    placeholder="eu-central-1"
                    className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Gönderici E-posta</Label>
                  <Input
                    value={mailSettings.sender_email || ""}
                    onChange={(e) => setMailSettings((prev) => ({ ...prev, sender_email: e.target.value }))}
                    placeholder="noreply@obihun.app"
                    className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Gönderici Adı</Label>
                  <Input
                    value={mailSettings.sender_name || ""}
                    onChange={(e) => setMailSettings((prev) => ({ ...prev, sender_name: e.target.value }))}
                    placeholder="ObiHub"
                    className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Reply-To</Label>
                  <Input
                    value={mailSettings.reply_to_email || ""}
                    onChange={(e) => setMailSettings((prev) => ({ ...prev, reply_to_email: e.target.value }))}
                    placeholder="destek@obihun.app"
                    className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">AWS Access Key ID</Label>
                  <Input
                    value={mailSettings.aws_access_key_id || ""}
                    onChange={(e) => setMailSettings((prev) => ({ ...prev, aws_access_key_id: e.target.value }))}
                    placeholder={mailSettings.aws_access_key_id_masked || "AKIA..."}
                    className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">AWS Secret Access Key</Label>
                  <Input
                    type="password"
                    value={mailSettings.aws_secret_access_key || ""}
                    onChange={(e) => setMailSettings((prev) => ({ ...prev, aws_secret_access_key: e.target.value }))}
                    placeholder={mailSettings.aws_secret_access_key_masked || "••••••••"}
                    className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Configuration Set (opsiyonel)</Label>
                  <Input
                    value={mailSettings.configuration_set_name || ""}
                    onChange={(e) => setMailSettings((prev) => ({ ...prev, configuration_set_name: e.target.value }))}
                    className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Source ARN (opsiyonel)</Label>
                  <Input
                    value={mailSettings.source_arn || ""}
                    onChange={(e) => setMailSettings((prev) => ({ ...prev, source_arn: e.target.value }))}
                    className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                  />
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button onClick={saveMailSettings} disabled={mailSettingsSaving} className="bg-sky-600 hover:bg-sky-500 text-white">
                  {mailSettingsSaving ? "Kaydediliyor..." : "SES Ayarlarını Kaydet"}
                </Button>
                <Input
                  value={testMailTo}
                  onChange={(e) => setTestMailTo(e.target.value)}
                  placeholder="test@ornek.com"
                  className="max-w-xs bg-slate-950 border-slate-700 text-slate-100"
                />
                <Button onClick={sendTestMail} disabled={testMailSending} variant="outline" className="border-slate-700 bg-slate-950 text-slate-100">
                  {testMailSending ? "Gönderiliyor..." : "Test Mail Gönder"}
                </Button>
              </div>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-base font-semibold">Mail Gönderim Logları</h3>
                <div className="flex items-center gap-2">
                  <Input
                    value={mailLogSearch}
                    onChange={(e) => setMailLogSearch(e.target.value)}
                    placeholder="E-posta / konu / hata ara"
                    className="h-9 w-56 bg-slate-950 border-slate-700 text-slate-100"
                  />
                  <select
                    value={mailLogStatus}
                    onChange={(e) => setMailLogStatus(e.target.value)}
                    className="h-9 rounded-md border border-slate-700 bg-slate-950 px-2 text-sm"
                  >
                    <option value="all">Tümü</option>
                    <option value="sent">sent</option>
                    <option value="failed">failed</option>
                    <option value="pending">pending</option>
                  </select>
                  <Button onClick={loadMailLogs} variant="outline" className="h-9 border-slate-700 bg-slate-950 text-slate-100">
                    Filtrele
                  </Button>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {mailLogsLoading ? (
                  <div className="text-sm text-muted-foreground">Yükleniyor...</div>
                ) : mailLogs.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Log bulunamadı.</div>
                ) : (
                  mailLogs.map((r) => (
                    <div key={r.id} className="rounded-lg border border-slate-800 bg-slate-950 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium truncate">{r.to_email || "-"}</div>
                        <span className="rounded bg-slate-800 px-2 py-0.5 text-xs">{r.status || "-"}</span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">{r.channel || "-"} • {r.provider || "-"}</div>
                      <div className="mt-1 text-sm text-slate-200 break-words">{r.subject || "-"}</div>
                      {r.error ? <div className="mt-1 text-xs text-rose-300 break-words">{r.error}</div> : null}
                      <div className="mt-2 text-[11px] text-muted-foreground">
                        Mesaj ID: {r.message_id || "-"} • {r.created_at || "-"}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          <div className="mt-4 grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold">Mail Şablonları</h3>
                <Button onClick={resetTemplateForm} variant="outline" className="h-8 border-slate-700 bg-slate-950 text-slate-100">
                  Yeni Şablon
                </Button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Örn: `incoming_e_invoice`, `new_order`, `referral_invite`</p>
              <div className="mt-3 space-y-2">
                {mailTemplatesLoading ? (
                  <div className="text-sm text-muted-foreground">Yükleniyor...</div>
                ) : mailTemplates.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Şablon yok.</div>
                ) : (
                  mailTemplates.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() =>
                        setTemplateForm({
                          id: t.id,
                          key: t.key || "",
                          name: t.name || "",
                          category: t.category || "custom",
                          description: t.description || "",
                          is_active: Boolean(t.is_active),
                          subject_template: t.subject_template || "",
                          html_template: t.html_template || "",
                          text_template: t.text_template || "",
                        })
                      }
                      className={`w-full rounded-lg border p-3 text-left ${
                        templateForm.id === t.id ? "border-sky-500 bg-sky-500/10" : "border-slate-800 bg-slate-950 hover:border-slate-700"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium truncate">{t.name || t.key}</div>
                        <span className={`rounded px-2 py-0.5 text-[10px] ${t.is_active ? "bg-emerald-900/30 text-emerald-300" : "bg-slate-800 text-muted-foreground"}`}>
                          {t.is_active ? "aktif" : "pasif"}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">{t.key}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{t.category}</div>
                    </button>
                  ))
                )}
              </div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <h3 className="text-base font-semibold">Şablon Düzenleyici</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-slate-300">Template Key</Label>
                  <Input
                    value={templateForm.key}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, key: e.target.value }))}
                    placeholder="incoming_e_invoice"
                    className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Ad</Label>
                  <Input
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Gelen E-Fatura Bilgilendirme"
                    className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Kategori</Label>
                  <Input
                    value={templateForm.category}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, category: e.target.value }))}
                    placeholder="invoice / order / referral / custom"
                    className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                  />
                </div>
                <div className="inline-flex items-center gap-2 self-end pb-1">
                  <input
                    type="checkbox"
                    checked={Boolean(templateForm.is_active)}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                  />
                  <span className="text-sm text-slate-300">Bu şablon aktif</span>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-slate-300">Açıklama</Label>
                  <Input
                    value={templateForm.description}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="Ne zaman tetikleneceği bilgisi"
                    className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-slate-300">Konu Şablonu</Label>
                  <Input
                    value={templateForm.subject_template}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, subject_template: e.target.value }))}
                    placeholder="Yeni Gelen E-Fatura Bilgilendirmesi - {{company_name}}"
                    className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-slate-300">HTML Şablonu</Label>
                  <textarea
                    value={templateForm.html_template}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, html_template: e.target.value }))}
                    className="mt-1 min-h-[200px] w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
                    placeholder={"<html><body><h3>Merhaba {{invitee_name}}</h3></body></html>"}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-slate-300">Text Şablonu (opsiyonel)</Label>
                  <textarea
                    value={templateForm.text_template}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, text_template: e.target.value }))}
                    className="mt-1 min-h-[120px] w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
                    placeholder={"Merhaba {{invitee_name}}\nPlan: {{plan}}\nLink: {{invite_url}}"}
                  />
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button onClick={saveTemplate} disabled={templateSaving} className="bg-sky-600 hover:bg-sky-500 text-white">
                  {templateSaving ? "Kaydediliyor..." : templateForm.id ? "Şablonu Güncelle" : "Şablon Oluştur"}
                </Button>
                <Input
                  value={templateTestTo}
                  onChange={(e) => setTemplateTestTo(e.target.value)}
                  placeholder="test@ornek.com"
                  className="max-w-xs bg-slate-950 border-slate-700 text-slate-100"
                />
                <Button onClick={sendTemplateTest} disabled={templateTestSending} variant="outline" className="border-slate-700 bg-slate-950 text-slate-100">
                  {templateTestSending ? "Gönderiliyor..." : "Template Test Gönder"}
                </Button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Kullanılabilir değişken örnekleri: <code>{"{{invitee_name}}"}</code>, <code>{"{{plan}}"}</code>, <code>{"{{invite_url}}"}</code>, <code>{"{{company_name}}"}</code>, <code>{"{{order_no}}"}</code>, <code>{"{{amount}}"}</code>, <code>{"{{invoice_no}}"}</code>
              </p>
            </div>
          </div>
          </>
          )}
          {settingsSection === "admin" && (
          <div className="mt-6 grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold">Super Admin Kullanıcıları</h3>
                <Button onClick={resetAdminUserForm} variant="outline" className="h-8 border-slate-700 bg-slate-950 text-slate-100">
                  Yeni
                </Button>
              </div>
              <div className="mt-3 space-y-2">
                {adminUsersLoading ? (
                  <div className="text-sm text-muted-foreground">Yükleniyor...</div>
                ) : adminUsers.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Super admin kullanıcı kaydı yok.</div>
                ) : (
                  adminUsers.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => setSelectedAdminUserId(u.id)}
                      className={`w-full rounded-lg border p-3 text-left ${
                        selectedAdminUserId === u.id ? "border-sky-500 bg-sky-500/10" : "border-slate-800 bg-slate-950 hover:border-slate-700"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium truncate">{u.full_name || u.email}</div>
                        <span className={`rounded px-2 py-0.5 text-[10px] ${u.is_active ? "bg-emerald-900/30 text-emerald-300" : "bg-rose-900/30 text-rose-300"}`}>
                          {u.is_active ? "aktif" : "pasif"}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">{u.email}</div>
                      <div className="mt-1 text-[11px] text-muted-foreground truncate">Yetkiler: {(u.permissions || []).join(", ") || "-"}</div>
                    </button>
                  ))
                )}
              </div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <h3 className="text-base font-semibold">Super Admin Erişim Ayarları</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-slate-300">E-posta</Label>
                  <Input value={adminUserForm.email} onChange={(e) => setAdminUserForm((p) => ({ ...p, email: e.target.value }))} className="mt-1 bg-slate-950 border-slate-700 text-slate-100" />
                </div>
                <div>
                  <Label className="text-slate-300">Ad Soyad</Label>
                  <Input value={adminUserForm.full_name} onChange={(e) => setAdminUserForm((p) => ({ ...p, full_name: e.target.value }))} className="mt-1 bg-slate-950 border-slate-700 text-slate-100" />
                </div>
                <div>
                  <Label className="text-slate-300">{selectedAdminUserId ? "Yeni Şifre (opsiyonel)" : "Şifre"}</Label>
                  <Input type="password" value={adminUserForm.password} onChange={(e) => setAdminUserForm((p) => ({ ...p, password: e.target.value }))} className="mt-1 bg-slate-950 border-slate-700 text-slate-100" />
                </div>
                <div className="inline-flex items-center gap-2 self-end pb-2">
                  <input type="checkbox" checked={Boolean(adminUserForm.is_active)} onChange={(e) => setAdminUserForm((p) => ({ ...p, is_active: e.target.checked }))} />
                  <span className="text-sm text-slate-300">Kullanıcı aktif</span>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-slate-300">Yetkiler (virgülle)</Label>
                  <Input value={adminUserForm.permissions_raw} onChange={(e) => setAdminUserForm((p) => ({ ...p, permissions_raw: e.target.value }))} placeholder="dashboard.view, logs.view, settings.manage" className="mt-1 bg-slate-950 border-slate-700 text-slate-100" />
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button onClick={saveAdminUser} disabled={adminUserSaving} className="bg-sky-600 hover:bg-sky-500 text-white">
                  {adminUserSaving ? "Kaydediliyor..." : selectedAdminUserId ? "Kullanıcıyı Güncelle" : "Kullanıcı Ekle"}
                </Button>
                <Button onClick={deleteAdminUser} disabled={!selectedAdminUserId} variant="outline" className="border-rose-700 bg-rose-900/20 text-rose-100">
                  Sil
                </Button>
              </div>
              <div className="mt-5 rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                <h4 className="text-sm font-semibold text-slate-200">Kendi Super Admin Hesabım</h4>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <Input value={myAccountForm.email} onChange={(e) => setMyAccountForm((p) => ({ ...p, email: e.target.value }))} placeholder="Kullanıcı adı/e-posta" className="bg-slate-950 border-slate-700 text-slate-100" />
                  <Input value={myAccountForm.full_name} onChange={(e) => setMyAccountForm((p) => ({ ...p, full_name: e.target.value }))} placeholder="Ad Soyad" className="bg-slate-950 border-slate-700 text-slate-100" />
                  <Input type="password" value={myAccountForm.current_password} onChange={(e) => setMyAccountForm((p) => ({ ...p, current_password: e.target.value }))} placeholder="Mevcut şifre" className="bg-slate-950 border-slate-700 text-slate-100" />
                  <Input type="password" value={myAccountForm.new_password} onChange={(e) => setMyAccountForm((p) => ({ ...p, new_password: e.target.value }))} placeholder="Yeni şifre" className="bg-slate-950 border-slate-700 text-slate-100" />
                  <Input type="password" value={myAccountForm.new_password_confirm} onChange={(e) => setMyAccountForm((p) => ({ ...p, new_password_confirm: e.target.value }))} placeholder="Yeni şifre tekrar" className="sm:col-span-2 bg-slate-950 border-slate-700 text-slate-100" />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button onClick={saveMySuperAccount} variant="outline" className="border-slate-700 bg-slate-900 text-slate-100">Kullanıcı Bilgisi Güncelle</Button>
                  <Button onClick={changeMySuperPassword} className="bg-amber-600 hover:bg-amber-500 text-white">Şifre Değiştir</Button>
                </div>
              </div>
            </div>
          </div>
          )}
          {settingsSection === "admin" && (
          <div className="mt-4">
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <h3 className="text-base font-semibold">Veri Araçları</h3>
              <p className="mt-1 text-xs text-muted-foreground">Tek seferlik veri düzeltme ve temizleme işlemleri.</p>
              <div className="mt-4 space-y-3">
                <DataFixTool
                  title="Muhasebe/POS Provider Düzelt"
                  description="Kullanıcıların provider alanını gerçekte bağlı olan entegrasyonla senkronize eder. BenimPOS bağlıyken hâlâ Paraşüt görenler için çalıştırın."
                  endpoint="/super/fix-accounting-provider"
                  method="POST"
                  resultKey="fixed"
                />
              </div>
            </div>
          </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}

function DataFixTool({ title, description, endpoint, method = "POST", resultKey = "fixed" }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const run = async () => {
    setRunning(true); setResult(null); setError("");
    try {
      const res = await api[method.toLowerCase()](endpoint);
      setResult(res);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Hata oluştu");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-950 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-200">{title}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">{description}</div>
          {result && (
            <div className="mt-2 rounded bg-emerald-900/20 border border-emerald-800 p-2 text-xs text-emerald-300">
              ✓ Tamamlandı — {result[resultKey]?.length ?? 0} kayıt düzeltildi
              {result[resultKey]?.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-emerald-400/80">
                  {result[resultKey].map((r, i) => (
                    <li key={i}>{r.user_id}: {r.old} → {r.new}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {error && (
            <div className="mt-2 rounded bg-rose-900/20 border border-rose-800 p-2 text-xs text-rose-300">{error}</div>
          )}
        </div>
        <Button
          onClick={run}
          disabled={running}
          variant="outline"
          className="shrink-0 h-8 border-slate-700 bg-slate-900 text-slate-100 text-xs"
        >
          {running ? "Çalışıyor..." : "Çalıştır"}
        </Button>
      </div>
    </div>
  );
}

function AiSettingsSection() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ anthropic_api_key: "", openai_api_key: "", gemini_api_key: "", preferred_provider: "anthropic" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/super/ai-settings").then(d => {
      setData(d);
      setForm(f => ({ ...f, preferred_provider: d.preferred_provider || "anthropic" }));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await api.put("/super/ai-settings", form);
      setMsg("Kaydedildi.");
      const d = await api.get("/super/ai-settings");
      setData(d);
      setForm(f => ({ ...f, anthropic_api_key: "", openai_api_key: "", gemini_api_key: "" }));
    } catch { setMsg("Hata oluştu."); }
    setSaving(false);
  };

  const PROVIDERS = [
    { key: "anthropic", label: "Anthropic (Claude)", field: "anthropic_api_key", placeholder: "sk-ant-..." },
    { key: "openai", label: "OpenAI (GPT)", field: "openai_api_key", placeholder: "sk-..." },
    { key: "gemini", label: "Google Gemini", field: "gemini_api_key", placeholder: "AIza..." },
  ];

  return (
    <div className="mt-6 max-w-xl">
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
        <h3 className="text-sm font-semibold text-slate-100 mb-4">AI Servis API Anahtarları</h3>
        <p className="text-xs text-slate-400 mb-5">
          AI ile zenginleştirme özelliği için en az bir provider'ın API key'i gereklidir.
          Boş bırakılan alanlar mevcut değeri korur.
        </p>

        <div className="mb-4">
          <label className="block text-xs text-slate-400 mb-1">Tercih Edilen Provider</label>
          <select
            value={form.preferred_provider}
            onChange={e => setForm(f => ({ ...f, preferred_provider: e.target.value }))}
            className="w-full rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm px-3 py-2"
          >
            {PROVIDERS.map(p => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>
        </div>

        {PROVIDERS.map(p => (
          <div key={p.key} className="mb-4">
            <label className="block text-xs text-slate-400 mb-1 flex items-center gap-2">
              {p.label}
              {data && data[`has_${p.key}`] && (
                <span className="inline-flex items-center gap-1 text-emerald-400 text-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block"></span>
                  Aktif: {data[`${p.key}_api_key`]}
                </span>
              )}
            </label>
            <input
              type="password"
              value={form[p.field]}
              onChange={e => setForm(f => ({ ...f, [p.field]: e.target.value }))}
              placeholder={data?.[`has_${p.key}`] ? "Değiştirmek için yeni key girin" : p.placeholder}
              className="w-full rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm px-3 py-2 placeholder:text-slate-600"
            />
          </div>
        ))}

        <div className="flex items-center gap-3 mt-2">
          <button
            onClick={save}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded"
          >
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
          {msg && <span className={`text-xs ${msg.includes("Hata") ? "text-red-400" : "text-emerald-400"}`}>{msg}</span>}
        </div>
      </div>
    </div>
  );
}
